import * as THREE from "three";
import React from "react";

import {
    addShadeEnabledRoofIds,
    CheckInsidePos,
    ensureRoofShadeReadyForModulePlacement,
    GenShadeOnFace,
    GetShadeCol,
    setShadeGatingState,
    SetModuleAsColored,
    SetModuleAsTextured,
    SetShadeDisplayEffect,
    RefreshTreeImpactArr,
    syncRayDelStatusForTrees,
    syncAllTreeRayDelStatusFromMeshes
} from "./RefreshShade";
import {
    BALLASTED_MODULE_RACKING_ANGLE,
    inch2m,
    inch4,
    moduleInfo,
    moduleSpacingInfoForBallastedRacking
} from "../../../../../Constants";
import {AlertType, ModuleOrientation} from "../../../../../@types/enums";
import {GetIdStr, GetShapePosArr, testMesh} from "./Loader";
import {
    GetClickObj,
    GetDis,
    GetMousePos,
    getCenterPoint,
    getRoofModuleCount,
    getRoofModuleMeshes,
    GetRoundNum,
    GetRadVal,
    ifRoofDeactivated, refreshContextMenu
} from "./Common";
import imgModulePort from '../../../../../assets/img/module/port.png';
import imgModuleLand from '../../../../../assets/img/module/land.jpg';
import {
    AddPathwayMesh,
    SetPathwayActiveModule, toggleSetbackIfMarkedDetachedForGivenBuilding,
    UpdateSetbackDepth
} from "./PathwayControl";
import {DeleteObst, SetObstVPos} from "./ObstructionControl";
import {reapplyAllTreeVisualStates} from "./TreeGhostVisual";
import {DoesIntersectOBB, SetMeshOBB, UpdateMeshOBB, UpdateWallMeshOBB} from "./MeshControl";
import {createLabel, getMultiplierLabelPrefix, isGhostOnlyGreenImpactTreesEnabled, UpdateTreeMuliplier} from "./EPCControl";
import {
    consumeGhostReevalDeferral,
    endManualGhostToggleIfIdle,
    enforceGreenImpactTreeRules,
    flushPendingUnghostAlert,
    isAiRunMode,
    shouldSkipGhostImpactReevaluation,
} from "./TreeImpactEligibility";
import {LineMaterial, LineSegments2, LineSegmentsGeometry} from "three-fatline";
import cloneDeep from 'lodash/cloneDeep';
import {logger} from "../../../../../services/loggingService";
import {isLightReachFinancePartner, syncLightReachSitePitchWarning} from "../../../../../helpers/lightReachWarning";
import {
    ACCESS_MULTIPLIERS,
    MODULE_GRAPH,
    SHADE_CALCULATIONS,
    siteCalculationState
} from "../../../../../services/siteCalculationState";
import {getAccessMultipliers, getIdealMultiplier} from "../../../../../services/MultiplierService";
import {useViewerStore} from "../../../../../store/store";
import { syncActiveAiDesignProductionFromCard } from "../../../../../helpers/aiDesignProductionSync";
import SelectBuildingsForModuleFill from "../../Modal/Modal-Content/SelectBuildingsForModuleFill";
import PolygonClipping from 'polygon-clipping';
import pointInPolygon from 'point-in-polygon';
import {Month} from "../../../../../domain/models/http/responses/GetIdealMultipliersResponse";
import {reBuildMonthlyWeightArrIfNotExist} from "./IrradianceControl";
import retry from "retry";
import {
    InstancedModuleObject
} from "@/components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/ModuleInstancedMeshControls";
import {
    isGroundMount
} from "@/components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/PlaneRoofControls";
import { EditAction } from "../../../../../@types/enums";
import {
    addModulesToAllRoofsMaxFill as addModulesToAllRoofsMaxFillTs,
    addModulesToRoofAutoFill as addModulesToRoofAutoFillTs,
    applyDebugOverlayVisibilityForFilling as applyDebugOverlayVisibilityTs,
    cleanPolygonForFilling as cleanPolygonForFillingTs,
    clearAllowedAreasOverlayForFilling as clearAllowedAreasOverlayTs,
    computeArrangementWithAutofillGuards as computeArrangementWithAutofillGuardsTs,
    computeBestArrangementForRoof as computeBestArrangementForRoofTs,
    getAllowedAreasForFilling as getAllowedAreasForFillingTs,
    getViewAlignedStartingCornerForFilling as getViewAlignedStartingCornerTs,
    precomputePredictedFillCounts as precomputePredictedFillCountsTs,
    renderAllowedAreasOverlayForFilling as renderAllowedAreasOverlayForFillingTs,
    generateModulePlacements
} from "./ModuleFilling";
import { ClearDebugDraw, DebugDrawManager, getOneDrawDebuggerOptions, isOneDrawDebugMode } from "./DebugDraw";
import { degToRad } from "three/src/math/MathUtils";

const {gap, snapDis, space} = useViewerStore.getState().isBallastedRackTypeModuleActive ? moduleSpacingInfoForBallastedRacking : moduleInfo;

const MIN_BALLAST_RACKING_ANGLE = 10;

function formatBallastRackingAngleLabel(angle, azimuth) {
    const normAz = ((Math.round(azimuth ?? 0) % 360) + 360) % 360;
    return `P-${angle}° / A-${normAz}°`;
}

function updateBallastAngleLabel(moduleMesh) {
    const ballastAngleLabel = moduleMesh.getObjectByName('ballastAngleLabel');
    if (!ballastAngleLabel?.element) return;
    const angle = moduleMesh.userData.ballastedPitch ?? moduleMesh.userData.ballastAngle;
    const azimuth = moduleMesh.userData.ballastedAzimuth;
    ballastAngleLabel.element.innerText = formatBallastRackingAngleLabel(angle, azimuth);
}

/** Module fill / graph timing. Enable: debug build, VITE_DEBUG_MODE, or `localStorage.setItem('onedraw_module_perf','1')`. */
function isModulePerfLogEnabled() {
    if (typeof window !== 'undefined' && window.localStorage?.getItem('onedraw_module_perf') === '1') return true;
    return isOneDrawDebugMode();
}

function perfNow() {
    return typeof performance !== 'undefined' ? performance.now() : 0;
}

function perfLog(label, ms, detail) {
    if (!isModulePerfLogEnabled()) return;
    const suffix = detail != null && detail !== '' ? ` ${detail}` : '';
    console.log(`[ModuleControl perf] ${label}: ${Number(ms).toFixed(1)}ms${suffix}`);
}

const moduleMapPort = new THREE.TextureLoader().load(imgModulePort);
const moduleMapLand = new THREE.TextureLoader().load(imgModuleLand);

export const selModuleCol = 0x00FF00, norModuleCol = 0xFFFFFF, errModuleCol = 0xFF0000, addModuleCol = 0x0000FF,
    timeModuleDelay = 300;

export function GetModuleMap(dir) {
    return dir === 'port' ? moduleMapPort : moduleMapLand;
}

export const ModulePlacementStatusOk = '';
export const ModulePlacementStatusRoofShape = 'roofShape';
export const ModulePlacementStatusModule = 'module';
export const ModulePlacementStatusSetback = 'setback';
export const ModulePlacementStatusPathway = 'pathway';
export const ModulePlacementStatusObstruction = 'obstruction';
export const ModulePlacementStatusWall = 'wall';
export const ModulePlacementStatusRoofPitch = 'roofPitch';
export const ModulePlacementStatusDeactivatedRoof = 'deactivatedRoof';
export const ModulePlacementStatusRoofPitchBallast = 'roofPitchBallastTedRacking';
const ALLOWED_AREAS_DEBUG_GROUP = 'allowedAreasDebugOverlay';
const DEFAULT_ALLOWED_AREAS_DEBUG_GROUP = 'defaultAllowedAreasDebugOverlay';
const DEFAULT_FILL_CORNER_DEBUG_GROUP = 'defaultFillCornerDebug';

function getModuleFillingContext() {
    return {
        THREE,
        MODULE_GRAPH,
        ModuleOrientation,
        useViewerStore,
        cloneDeep,
        siteCalculationState,
        isOneDrawDebugMode,
        GetModuleFaceArr,
        GetSetPathArr,
        GetAllowedAreas,
        renderAllowedAreasOverlay,
        pointInPolygon,
        PolygonClipping,
        getOneDrawDebuggerOptions,
        getRoofModuleMeshes,
        SetObstVPos,
        initForModulePlacement,
        clearDefaultFillCornerDebug,
        renderDefaultFillStartCornerDebug,
        isEligibleRoofForModules,
        isRoofTooSteep,
        ensureModeInfoEntry,
        focusCameraToRoofForFill,
        updateModeArrInfoForRoof,
        addModulesToScene,
        applyPostProcessing,
        UpdateSetbackDepth,
        SetPathwayActiveModule,
        AddIndividualMode,
        DeleteModule,
        ALLOWED_AREAS_DEBUG_GROUP,
        DEFAULT_ALLOWED_AREAS_DEBUG_GROUP,
        DEFAULT_FILL_CORNER_DEBUG_GROUP,
        GetLimitPosValue,
    };
}


export function GetSetPathArr(roofGroup, roofId, force36Setback = false) {
    const pathwayArr = [], setbackArr = [];
    const sceneViewer = useViewerStore.getState().sceneViewer;
    const roofFace = sceneViewer?.roofs?.[0]?.faces?.find((face) => face.roofFaceId === roofId);
    const lineActiveById = new Map((roofFace?.lines || []).map((line) => [line.lineId, !!line.active]));
    roofGroup.traverse(child => {
        if (child.pathWayRoofId === roofId) {
            const {meshType, pathwayPosArr, pathwayPosArr18, pathwayId, pathActive} = child;
            if (meshType === 'pathway') {
                const lineActive = lineActiveById.has(pathwayId) ? lineActiveById.get(pathwayId) : undefined;
                pathwayArr.push({
                    type: meshType,
                    posArr: pathwayPosArr,
                    pathwayId,
                    // Prefer roof face line.active (authoritative), fallback to mesh state.
                    pathActive: lineActive !== undefined ? lineActive : pathActive,
                });
            }
            else if (meshType === 'setback') {
                if (child.isMarkedInactive) return;
                if (child.userData.use18 && !force36Setback) {
                    setbackArr.push({type: meshType, posArr: pathwayPosArr18})
                }
                else {
                    setbackArr.push({type: meshType, posArr: pathwayPosArr})
                }
            }
        }
    })
    return {pathwayArr, setbackArr};
}

export function initForModulePlacement(self) {
    const _perfT0 = perfNow();
    let isFlatRoof = false;
    const {roofMeshArr, obstGroup} = self;
    const {faces} = self.roofs[0];

    const selSize = useViewerStore.getState().activeModule;

    moduleMapPort.anisotropy = self.renderer.capabilities.getMaxAnisotropy();
    moduleMapLand.anisotropy = self.renderer.capabilities.getMaxAnisotropy();

    const buildingArr = [];

    roofMeshArr.forEach(roofMesh => {
        roofMesh.obstArr = [];
        for (let i = roofMesh.children.length - 1; i >= 0; i--) {
            const childMesh = roofMesh.children[i];
            if (childMesh.module) {
                const meshIdx = self.meshArr.findIndex(item => {
                    return item.moduleId === childMesh.moduleId
                });
                self.meshArr.splice(meshIdx, 1); // shift
                roofMesh.remove(childMesh);
            }
        }
        const {sizeM} = roofMesh, {buildingName} = roofMesh.userData;
        if (roofMesh.flatRoof === true) {
            isFlatRoof = true;
            roofMesh.userData.roof_size_type = "flat_face"
            roofMesh.userData.roofSquare = {
                lowSquare: null,
                normalSquare: null,
            }
        }
        var existBuilding = false;
        buildingArr.forEach(item => {
            if (item.name === buildingName) {
                existBuilding = true;
                item.size += sizeM;
                item.sizeSqFt += sizeM * 10.7639;
            }
        });
        if (!existBuilding) {
            buildingArr.push({name: buildingName, size: sizeM, sizeSqFt: sizeM * 10.7639, isFlatRoof: isFlatRoof});
        }
    });

    buildingArr.forEach(building => {

        const correctedModuleWidth = selSize.w + inch2m;
        const correctedModuleHeight = selSize.h + inch2m;

        const moduleWidthInInch = correctedModuleWidth * 39.3701
        const moduleHeightInInch = correctedModuleHeight * 39.3701

        const moduleAreaInSqInch = moduleWidthInInch * moduleHeightInInch
        const buildingAreaInSqInch = building.size * 1550

        building.moduleArea = moduleAreaInSqInch / 144;
        building.modeCount33 = Math.ceil((buildingAreaInSqInch * 0.33) / moduleAreaInSqInch)
    });
    useViewerStore.setState({buildingWise33PercentRuleModuleCountList: buildingArr});

    self.buildingArr = [...buildingArr];
    self.moduleMeshArr = [];
    obstGroup.children.forEach((obst) => {
        const obstMesh = obst.children[0].children[0].children[0];
        const {roofId, roofPos, radius, shapeNum, width, length, verPosArr, selfRot} = obstMesh;
        if (!roofId || !roofPos) return;
        const selRoof = roofMeshArr.find(item => item.roofFaceId === roofId);
        selRoof.obstArr.push({roofPos, radius, shapeNum, width, length, verPosArr, selfRot});
    });

    roofMeshArr.forEach(roofMesh => {
        const {posArr, rotX, rotY, obstArr} = roofMesh,
            lastNode = posArr[posArr.length - 1];

        const obstPosArr = [], disGap = 0.1;
        obstArr.forEach(obst => {
            obstPosArr.push(obst.roofPos);
        });
        const newPos2DInfo = GetShapePosArr([...obstPosArr, ...posArr], -rotX, -rotY, lastNode);

        for (let i = 0; i < obstArr.length; i++) {
            const cPos = newPos2DInfo.pos2DArr[i], verArr = [], {radius, shapeNum, width, length, selfRot} = obstArr[i];
            const xDis = shapeNum === 1 ? width : radius, xDelta = xDis / 2 + disGap,
                zDis = shapeNum === 1 ? length : radius, zDelta = zDis / 2 + disGap;
            if (selfRot) {
                const cubeR = GetDis({x: 0, z: 0}, {x: xDelta, z: zDelta}), ang = Math.atan2(xDelta, zDelta);
                [ang, -ang, Math.PI + ang, Math.PI - ang].forEach(val => {
                    const rotX = cPos.x + Math.sin(val + selfRot) * cubeR,
                        rotZ = cPos.z + Math.cos(val + selfRot) * cubeR;
                    verArr.push({x: rotX, z: rotZ});
                });
            } else {
                [-1, 1].forEach(xDir => {
                    [-1, 1].forEach(zDir => {
                        verArr.push({x: cPos.x + xDir * xDelta, z: cPos.z + zDir * zDelta})
                    });
                });
            }
            const x0 = cPos.x - xDelta, z0 = cPos.z - zDelta,
                x1 = cPos.x + xDelta, z1 = cPos.z + zDelta;
            obstArr[i].cPos = cPos;
            obstArr[i].verArr = verArr;
            obstArr[i].verInfo = {x0, z0, x1, z1};
        }
    });

    perfLog('initForModulePlacement', perfNow() - _perfT0, `roofs=${self.roofMeshArr?.length ?? 0}`);
}


// --- helpers --- //

function isEligibleRoofForModules(roofMesh, accessoryBuildingList) {
    if (!roofMesh) return false;
    if (roofMesh.disableModule) return false;
    if (accessoryBuildingList?.includes(roofMesh.userData?.buildingName)) return false;
    return true;
}

function ensureModeInfoEntry(modeArrInfo, roofFaceId) {
    const existing = modeArrInfo.find(r => r.roofFaceId === roofFaceId);
    if (!existing) modeArrInfo.push({ roofFaceId, moduleArr: [] });
}

function updateModeArrInfoForRoof(modeArrInfo, roofFaceId, moduleArr) {
    const idx = modeArrInfo.findIndex((r) => r.roofFaceId === roofFaceId);
    if (idx !== -1) {
        modeArrInfo[idx].moduleArr = moduleArr;
    } else {
        modeArrInfo.push({ roofFaceId, moduleArr });
    }
}

function addModulesToScene(roofMesh, moduleArr, self, showLabel) {
    for (const item of moduleArr) {
        AddIndividualMode(roofMesh, item, true, self.moduleMeshArr, self.meshArr, showLabel);
    }
}

function syncPathwayActiveStateBeforeMaxfill(self) {
    const faces = self?.roofs?.[0]?.faces || [];
    faces.forEach((face) => {
        const lines = face?.lines || [];
        lines.forEach((line) => {
            if (!line?.lineId) return;
            SetPathwayActiveModule(self, line.lineId);
        });
    });
}

function clearDefaultFillCornerDebug(roofMesh) {
    const sceneViewer = useViewerStore.getState().sceneViewer;
    if (!sceneViewer) return;
    DebugDrawManager.execute(sceneViewer, 'clearDefaultFillCorner', { roofMesh });
}

function renderDefaultFillStartCornerDebug(roofMesh, pos2DArr, cornerKey) {
    const sceneViewer = useViewerStore.getState().sceneViewer;
    if (!sceneViewer || !roofMesh || !pos2DArr?.length || !cornerKey) return;
    DebugDrawManager.execute(sceneViewer, 'renderDefaultFillStartCorner', { roofMesh, pos2DArr, cornerKey });
}

function focusCameraToRoofForFill(self, roofMesh) {
    if (!self?.camera || !self?.controls || !roofMesh?.posArr?.length) return;
    const { camera, controls } = self;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    roofMesh.posArr.forEach(({ x, z }) => {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (z < minZ) minZ = z;
        if (z > maxZ) maxZ = z;
    });
    if (!Number.isFinite(minX) || !Number.isFinite(minZ)) return;

    const deltaDis = 40;
    const roofRot = (roofMesh.oriAng?.azimuth || 0) * Math.PI / 180;
    const targetRot = Math.PI - roofRot;
    const conPos = { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2 };
    const dir = { x: Math.sin(targetRot), z: Math.cos(targetRot) };
    const center = getCenterPoint(roofMesh);
    const zoomTar = 1000 / Math.max(maxX - minX, maxZ - minZ);

    camera.zoom = zoomTar;
    camera.position.set(conPos.x + deltaDis * dir.x, camera.position.y, conPos.z + deltaDis * dir.z);
    controls.target.set(center.x, center.y, center.z);
    camera.lookAt(new THREE.Vector3(center.x, center.y, center.z));
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld(true);
    controls.update();
}

/**
 * Compute the best module arrangement for a roof by trying orientations from all corners.
 */
function computeBestArrangementForRoof(
    roofMesh,
    pos2DArr,
    holeArr,
    rotX,
    setbackArr,
    pathwayArr,
    selSize,
    selDir,
    obstMeshArr,
    wallMeshArr,
    maxCount = Infinity,
) {
    const t0 = perfNow();
    const result = computeBestArrangementForRoofTs(getModuleFillingContext(), {
        roofMesh,
        pos2DArr,
        holeArr,
        rotX,
        setbackArr,
        pathwayArr,
        selSize,
        selDir,
        obstMeshArr,
        wallMeshArr,
    });
    const ms = perfNow() - t0;
    if (isModulePerfLogEnabled() && ms >= 50) {
        perfLog(
            'computeBestArrangementForRoof(slow)',
            ms,
            `roof=${roofMesh?.roofFaceId} dir=${selDir ?? 'n/a'} modules=${result?.length ?? 0}`
        );
    }
    return result;
}

function computeArrangementWithAutofillGuards(
    self,
    roofMesh,
    selSize,
    selDir,
    obstMeshArr,
    wallMeshArr,
    renderDebugAllowedAreas = false,
    debugVariant = 'autofill'
) {
    const t0 = perfNow();
    const result = computeArrangementWithAutofillGuardsTs(getModuleFillingContext(), {
        self,
        roofMesh,
        selSize,
        selDir,
        obstMeshArr,
        wallMeshArr,
        renderDebugAllowedAreas,
        debugVariant,
    });
    const ms = perfNow() - t0;
    if (isModulePerfLogEnabled() && ms >= 50) {
        perfLog(
            'computeArrangementWithAutofillGuards(slow)',
            ms,
            `roof=${roofMesh?.roofFaceId} dir=${selDir ?? 'n/a'} variant=${debugVariant} modules=${result?.length ?? 0}`
        );
    }
    return result;
}

// Apply final persistence + updates used at the end of SetModuleArr
function applyPostProcessing(self, modeArrInfo, type) {
    // Persist layout
    useViewerStore.setState({ savedRoofModulesLayout: modeArrInfo });

    // Site/pathway updates
    siteCalculationState.needsUpdate(MODULE_GRAPH);
    // Update setback depths (keeps same signature as before)
    UpdateSetbackDepth(self, type, self.moduleDir);

    if (type !== 'moduleCount') {
        // Update pathway active modules for each roof line
        for (const roofMesh of self.roofMeshArr) {
            if (roofMesh.disableModule || isGroundMount(roofMesh)) continue;
            const face = self.roofs[0].faces.find(f => f.roofFaceId === roofMesh.roofFaceId);
            const { lines } = face || {};
            lines?.forEach((line) => {
                SetPathwayActiveModule(self, line.lineId);
            });
        }
    }

    // UI updates (same as original)
    SetBtnDirClass(self.moduleDir);
    const selSize = useViewerStore.getState().activeModule;
    SetVirModuleSize(self.virModule, 'size', selSize);
    SetVirModuleSize(self.virModule, 'dir', self.moduleDir);
    DeleteSingleModuleOnRoof(self);

    // Delay shade effect update (preserve timeout behaviour)
    setTimeout(() => {
        self.modelGroup.irrType = null;
        SetShadeDisplayEffect(self.modelGroup);
    }, 100);
}


/**
 * Update module count in userdata for given roof(s)
 *    - self: viewer context
 *    - selDir: orientation key used as index in userData.moduleCount (can be null)
 *    - allRoofs: if true update all eligible roofs; if false update only the roof identified by selRoofId
 *    - selRoofId: roofFaceId string (used only when allRoofs === false)
 */
export function updateModuleCount(self, selDir, allRoofs = true, selRoofId = null) {
    const _perfT0 = perfNow();
    const { roofMeshArr, accessoryBuildingList } = self;

    // Ensure obstacle vertical positions match original
    SetObstVPos(self.obstGroup);

    const counts = {};
    for (const roofMesh of roofMeshArr) {
        if (!allRoofs && roofMesh.roofFaceId !== selRoofId) continue;
        if (!isEligibleRoofForModules(roofMesh, accessoryBuildingList)) continue;

        // Skip very small roofs or too steep roofs
        if (roofMesh.size && roofMesh.size < 36.11111) continue;
        if (isRoofTooSteep(roofMesh.oriAng?.tilt)) continue;

        const { roofFaceId } = roofMesh;
        const roofT0 = perfNow();

        // Prepare obst and wall arrays like original
        const obstMeshArr = self.obstGroup.children.map((c) => c.children[0].children[0].children[0]);
        const wallMeshArr = self.wallMeshArr;

        const moduleArr = computeArrangementWithAutofillGuards(
            self,
            roofMesh,
            useViewerStore.getState().activeModule,
            selDir,
            obstMeshArr,
            wallMeshArr
        );

        if (!roofMesh.userData.moduleCount) roofMesh.userData.moduleCount = {};
        roofMesh.userData.moduleCount[selDir] = moduleArr.length;
        counts[roofFaceId] = moduleArr.length;
        const roofMs = perfNow() - roofT0;
        if (isModulePerfLogEnabled() && roofMs >= 80) {
            perfLog('updateModuleCount roof', roofMs, `${roofFaceId} dir=${selDir ?? 'n/a'} count=${moduleArr.length}`);
        }
    }
    perfLog(
        'updateModuleCount total',
        perfNow() - _perfT0,
        `dir=${selDir ?? 'n/a'} allRoofs=${allRoofs} roofsIterated=${Object.keys(counts).length}`
    );
}

const LARGE_DESIGN_MODULE_THRESHOLD = 200;

/**
 * Compute estimated module counts per building for max fill (no scene updates).
 * Returns { buildingRows: [{ buildingName, roofFaceIds, estimatedCount }], total }.
 */
export function getEstimatedModulesForMaxFill(self, selSize = null, selDir = null) {
    const _perfT0 = perfNow();
    const { roofMeshArr, roofGroup, obstGroup, wallMeshArr, accessoryBuildingList } = self;
    SetObstVPos(obstGroup);
    if (!selSize) selSize = useViewerStore.getState().activeModule;

    const byBuilding = new Map(); // buildingName -> { buildingName, roofFaceIds: [], estimatedCount: 0 }
    let total = 0;
    let roofsComputed = 0;

    for (const roofMesh of roofMeshArr) {
        if (!isEligibleRoofForModules(roofMesh, accessoryBuildingList)) continue;
        if (roofMesh.size && roofMesh.size < 36.11111) continue;
        const { oriAng } = roofMesh;
        const { tilt } = oriAng || {};
        if (isRoofTooSteep(tilt)) continue;

        const roofFaceId = roofMesh.roofFaceId;
        const { rotX, pos2DArr, holeArr } = roofMesh;
        const { pathwayArr, setbackArr } = GetSetPathArr(roofGroup, roofFaceId);
        const obstMeshArr = obstGroup.children.map((c) => c.children[0].children[0].children[0]);
        const moduleArr = computeBestArrangementForRoof(
            roofMesh,
            pos2DArr,
            holeArr,
            rotX,
            setbackArr,
            pathwayArr,
            selSize,
            selDir,
            obstMeshArr,
            wallMeshArr
        );
        const count = moduleArr.length;
        const buildingName = roofMesh.userData?.buildingName ?? roofFaceId;
        if (!byBuilding.has(buildingName)) {
            byBuilding.set(buildingName, { buildingName, roofFaceIds: [], estimatedCount: 0 });
        }
        const row = byBuilding.get(buildingName);
        row.roofFaceIds.push(roofFaceId);
        row.estimatedCount += count;
        total += count;
        roofsComputed++;
    }

    const buildingRows = Array.from(byBuilding.values());
    perfLog(
        'getEstimatedModulesForMaxFill',
        perfNow() - _perfT0,
        `roofsComputed=${roofsComputed} totalModulesEst=${total} buildings=${buildingRows.length}`
    );
    return { buildingRows, total };
}

function getBuildingReferenceMarkers(self, buildingRows) {
    const { roofMeshArr, roofGroup, accessoryBuildingList } = self;
    if (!roofGroup || !roofMeshArr?.length || !buildingRows?.length) return [];

    const siteBoundingBox = new THREE.Box3().setFromObject(roofGroup);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    siteBoundingBox.getSize(size);
    siteBoundingBox.getCenter(center);

    const maxDimension = Math.max(size.x, size.z);
    const halfViewSize = (maxDimension / 2) * 1.2;
    if (!halfViewSize) return [];

    const meshByRoofFaceId = new Map();
    roofMeshArr.forEach((mesh) => {
        if (!isEligibleRoofForModules(mesh, accessoryBuildingList)) return;
        meshByRoofFaceId.set(mesh.roofFaceId, mesh);
    });

    const clampPct = (value) => Math.min(98, Math.max(2, value));

    return buildingRows
        .map((row) => {
            const centers = row.roofFaceIds
                .map((roofFaceId) => meshByRoofFaceId.get(roofFaceId))
                .filter(Boolean)
                .map((mesh) => {
                    const roofBox = new THREE.Box3().setFromObject(mesh);
                    const roofCenter = new THREE.Vector3();
                    roofBox.getCenter(roofCenter);
                    return roofCenter;
                });

            if (!centers.length) return null;

            const avgX = centers.reduce((sum, vector) => sum + vector.x, 0) / centers.length;
            const avgZ = centers.reduce((sum, vector) => sum + vector.z, 0) / centers.length;
            const xPct = clampPct((((avgX - center.x) / halfViewSize) + 1) * 50);
            const yPct = clampPct((((avgZ - center.z) / halfViewSize) + 1) * 50);

            return { label: String(row.buildingName), xPct, yPct };
        })
        .filter(Boolean);
}

/**
 *  Add modules to all roofs in scene (default) using 'max fill' logic.
 *    - self: viewer context
 *    - selSize: module size to use (if null, uses useViewerStore.activeModule)
 *    - selDir: optional forced orientation
 *    - selectedRoofFaceIds: optional array; when set, only these roofs are filled (used after building-selection modal)
 *
 * When estimated modules > LARGE_DESIGN_MODULE_THRESHOLD (200), a modal is shown to select which buildings to fill.
 */
export function addModulesToAllRoofsMaxFill(self, selSize = null, selDir = null, selectedRoofFaceIds = null, options = {}) {
    const { skipShadePrewarm = false } = options ?? {};
    const _perfT0All = perfNow();
    // If no selection filter provided, check total estimate and maybe show building-selection modal
    if (selectedRoofFaceIds === null || selectedRoofFaceIds === undefined) {
        const { buildingRows, total } = getEstimatedModulesForMaxFill(self, selSize, selDir);
        if (total <= LARGE_DESIGN_MODULE_THRESHOLD) {
            setShadeGatingState(false, []);
        }
        if (total > LARGE_DESIGN_MODULE_THRESHOLD) {
            setShadeGatingState(true, []);
            let referenceImageDataUrl;
            let referenceMarkers = [];
            if (typeof self.getSquareImageOfSite === 'function') {
                const wasMapLayerVisible = useViewerStore.getState().layerVisibilityState?.maps ?? true;
                try {
                    // Hide map layer during capture so white map planes do not obscure buildings.
                    if (typeof self.setVisibleGroup === 'function') self.setVisibleGroup("maps", false);
                    if (self.mapCustom) self.mapCustom.visible = false;

                    referenceImageDataUrl = self.getSquareImageOfSite(false);
                    referenceMarkers = getBuildingReferenceMarkers(self, buildingRows);
                } catch (error) {
                    console.warn('Failed to generate top-view reference image for building selection modal.', error);
                } finally {
                    if (wasMapLayerVisible) {
                        if (typeof self.setVisibleGroup === 'function') self.setVisibleGroup("maps", true);
                        if (self.mapCustom) self.mapCustom.visible = true;
                    }
                }
            }
            self.viewerAlert({
                show: true,
                title: 'Select buildings to fill with modules',
                message: React.createElement(SelectBuildingsForModuleFill, {
                    buildingRows,
                    total,
                    referenceImageDataUrl,
                    referenceMarkers,
                    onConfirm: (selected) => {
                        setShadeGatingState(true, selected);
                        addModulesToAllRoofsMaxFill(self, selSize, selDir, selected);
                        self.removeViewerAlert();
                    },
                    onCancel: () => self.removeViewerAlert(),
                }),
                messageType: AlertType.warning,
                isModal: true,
            });
            perfLog('addModulesToAllRoofsMaxFill (modal)', perfNow() - _perfT0All, 'returned early — user must pick buildings');
            return;
        }
    }

    if (selectedRoofFaceIds && selectedRoofFaceIds.length > 0) {
        const tShade = perfNow();
        addShadeEnabledRoofIds(selectedRoofFaceIds);
        useViewerStore.setState({ isBgCalc: true });
        // Skip the shade pre-warm when the caller runs its own single authoritative shade pass
        // afterwards (AI design apply flow) — otherwise shade is computed twice for one selection.
        if (!skipShadePrewarm) {
            const selectedFacesForShade = self.roofs?.[0]?.faces?.filter((face) =>
                selectedRoofFaceIds.includes(face.roofFaceId)
            ) || [];
            const pendingFacesForShade = selectedFacesForShade.filter((face) => {
                const roofMesh = self.roofMeshArr.find((roof) => roof.roofFaceId === face.roofFaceId);
                const hasShadeGeometry = Array.isArray(roofMesh?.userData?.shadeArrForRoof) && roofMesh.userData.shadeArrForRoof.length > 0;
                const hasShadeHash = Array.isArray(face?.ptHashArr) && face.ptHashArr.length > 0;
                return !(hasShadeGeometry && hasShadeHash);
            });
            if (pendingFacesForShade.length > 0) {
                GenShadeOnFace(pendingFacesForShade, self);
            }
            perfLog('addModulesToAllRoofsMaxFill GenShadeOnFace branch', perfNow() - tShade, `pendingFaces=${pendingFacesForShade.length}`);
        }
    }

    const tFill = perfNow();
    addModulesToAllRoofsMaxFillTs(
        getModuleFillingContext(),
        self,
        selSize,
        selDir,
        selectedRoofFaceIds
    );
    perfLog('addModulesToAllRoofsMaxFillTs', perfNow() - tFill, `selectedFaces=${selectedRoofFaceIds?.length ?? 'all'}`);
    perfLog('addModulesToAllRoofsMaxFill total', perfNow() - _perfT0All, '');
}

/**
 * Max-fill only the listed roof faces (dashboard / AI layout variants). Same pipeline as
 * {@link addModulesToAllRoofsMaxFill} with `selectedRoofFaceIds` set.
 */
export function addModulesToRoofsByIds(self, roofFaceIds, selDir = null, options = {}) {
    if (!roofFaceIds || !roofFaceIds.length) return;
    addModulesToAllRoofsMaxFill(self, null, selDir ?? null, roofFaceIds, options);
}

/**
 * After {@link SetModuleGraph}: on the given roofs, optionally delete modules whose ideal-relative
 * efficiency (%) is below `minEffThreshold`, then delete any remaining single-module layouts on those roofs.
 */
export function removeGreyModulesAndSingleModuleRoofs(self, roofFaceIds, minEffThreshold) {
    if (!self?.roofMeshArr || !roofFaceIds || !roofFaceIds.length) return;
    const idSet = new Set(roofFaceIds);

    if (minEffThreshold != null && minEffThreshold > 0) {
        const treeArr = [];
        self.treeGroup?.children?.forEach((t) => treeArr.push(t.userData.treeId));

        const angIdeal = cloneDeep(useViewerStore.getState().savedRoofWiseIdealMultiplier);
        angIdeal.forEach((ang) => {
            ang.tilt = parseFloat(ang.tilt);
            ang.azimuth = parseFloat(ang.azimuth);
            ang.ideal = ang.idealMonthly ? parseFloat(ang.ideal) : parseFloat(ang.ideal / 10000);
        });
        const angArr = useViewerStore.getState().savedAccessMultipliersForSolarRoofs;
        const facePtInfo = cloneDeep(useViewerStore.getState().savedRoofWiseAccessMultiplierDataArray);
        facePtInfo.forEach((face) => {
            face.size = parseFloat(face.size);
            const ptVal = parseInt(face.ptHashAverage, 10);
            const azimuth = GetRoundNum(parseFloat(face.azimuth), 4);
            const tilt = GetRoundNum(parseFloat(face.tilt), 2);
            const selProdAng = angArr.find(
                (item) =>
                    GetRoundNum(item.azimuth, 4) === azimuth &&
                    GetRoundNum(item.tilt, 2) === tilt &&
                    item.multipliers,
            );
            const multipliers = selProdAng?.multipliers ?? [];
            const prodInfo = multipliers.find((item) => item.access === ptVal);
            face.multiplier = prodInfo ? prodInfo.multiplier : 0;
            face.multipliers = multipliers;
            const selIdealAng = angIdeal.find((item) => GetRoundNum(item.tilt, 2) === tilt);
            face.idealMulti = selIdealAng ? selIdealAng.ideal : 0;
        });

        self.roofMeshArr.forEach((roofMesh) => {
            if (!idSet.has(roofMesh.roofFaceId)) return;
            if (roofMesh.disableModule || roofMesh.userData.roof_size_type === 'small_face') return;
            const faceInfo = facePtInfo.find((f) => f.roofFaceId === roofMesh.roofFaceId);
            if (!faceInfo?.multipliers) return;
            const { idealMulti, multipliers } = faceInfo;
            const shadeArr = roofMesh.userData.shadeArrForRoof || [];
            const modules = getRoofModuleMeshes(roofMesh);
            if (!modules.length) return;
            const toRemove = [];
            modules.forEach((module) => {
                const { irrPro } = GetIrrPro(module.verInfo, shadeArr, treeArr);
                const prodInfo = multipliers.find((item) => item.access === Math.floor(irrPro));
                const module_multiplier = prodInfo?.multiplier ?? 0;
                const effPct = idealMulti ? (module_multiplier / idealMulti) * 100 : 0;
                if (effPct < minEffThreshold) toRemove.push(module);
            });
            if (toRemove.length) DeleteModule(toRemove, roofMesh, self.moduleMeshArr, self);
        });
    }

    self.roofMeshArr.forEach((roofMesh) => {
        if (!idSet.has(roofMesh.roofFaceId)) return;
        const modules = getRoofModuleMeshes(roofMesh);
        if (modules.length === 1) {
            DeleteModule(modules, roofMesh, self.moduleMeshArr, self);
        }
    });
}

/**
 *  Add modules to a given roof (autofill)
 *    - self: viewer context
 *    - roofMesh: the specific roof mesh to fill
 *    - selSize: module size (if null, uses store)
 *    - selDir: optional forced orientation
 *    - removeExisting: if true, deletes any existing modules on that roof before adding
 */
export async function addModulesToRoofAutoFill(
    self,
    roofMesh,
    selSize,
    selDir,
    options = {}
) {
    const _perfT0 = perfNow();
    useViewerStore.setState({ isBgCalc: true });
    const tShade = perfNow();
    await ensureRoofShadeReadyForModulePlacement(self, roofMesh.roofFaceId);
    perfLog('addModulesToRoofAutoFill ensureRoofShadeReady', perfNow() - tShade, roofMesh?.roofFaceId);
    const tTs = perfNow();
    const out = await addModulesToRoofAutoFillTs(
        getModuleFillingContext(),
        self,
        roofMesh,
        selSize,
        selDir,
        options
    );
    perfLog('addModulesToRoofAutoFillTs', perfNow() - tTs, roofMesh?.roofFaceId);
    perfLog('addModulesToRoofAutoFill total', perfNow() - _perfT0, roofMesh?.roofFaceId);
    return out;
}

const MODULE_FILL_SIM_EVENT = 'module-fill-simulation';
const DEFAULT_SIMULATION_DELAY_MS = 140;

function emitModuleFillSimulationEvent(payload) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(MODULE_FILL_SIM_EVENT, { detail: payload }));
}

function clearModuleFillSimulationTimer(self) {
    if (!self?.moduleFillSimulationTimer) return;
    clearTimeout(self.moduleFillSimulationTimer);
    self.moduleFillSimulationTimer = null;
}

function finalizeRoofAutofill(self, roofMesh, face, selDir) {
    siteCalculationState.needsUpdate(MODULE_GRAPH);
    UpdateSetbackDepth(self, 'autoFill', selDir);
    const { lines } = face || {};
    lines?.forEach((line) => SetPathwayActiveModule(self, line.lineId));

    roofMesh.children
        .filter((item) => item.meshType === 'module')
        .forEach((module) => {
            module.wPos = module.getWorldPosition(new THREE.Vector3());
        });
}

function simulateRoofAutofill(self, roofMesh, moduleArr, face, selDir, showLabel, simulationDelayMs) {
    clearModuleFillSimulationTimer(self);

    const total = moduleArr.length;
    const roofNumber =
        roofMesh?.userData?.roof_tag_id ??
        roofMesh?.userData?.roof_number_from_aurora ??
        roofMesh?.roofFaceId;
    const obstructionInfo = roofMesh?.userData?.fillDebugObstructionInfo || {};
    const obstructionIds = Array.isArray(obstructionInfo.obstructionIds) ? obstructionInfo.obstructionIds : [];
    const obstructionHitCount = Number(obstructionInfo.obstructionHitCount || 0);
    emitModuleFillSimulationEvent({
        phase: 'start',
        roofFaceId: roofMesh?.roofFaceId,
        roofNumber,
        total,
        obstructionIds,
        obstructionHitCount,
        delayMs: simulationDelayMs,
    });

    if (!total) {
        finalizeRoofAutofill(self, roofMesh, face, selDir);
        emitModuleFillSimulationEvent({
            phase: 'complete',
            roofFaceId: roofMesh?.roofFaceId,
            roofNumber,
            total,
            placed: 0,
            obstructionIds,
            obstructionHitCount,
        });
        return;
    }

    let index = 0;
    const placeNext = () => {
        if (index >= total) {
            self.moduleFillSimulationTimer = null;
            finalizeRoofAutofill(self, roofMesh, face, selDir);
            emitModuleFillSimulationEvent({
                phase: 'complete',
                roofFaceId: roofMesh?.roofFaceId,
                roofNumber,
                total,
                placed: total,
                obstructionIds,
                obstructionHitCount,
            });
            return;
        }

        const item = moduleArr[index];
        AddIndividualMode(roofMesh, item, true, self.moduleMeshArr, self.meshArr, showLabel);

        emitModuleFillSimulationEvent({
            phase: 'step',
            roofFaceId: roofMesh?.roofFaceId,
            roofNumber,
            total,
            index: index + 1,
            x: item?.x,
            z: item?.z,
            dir: item?.dir,
            moduleId: item?.moduleId,
            obstructionIds,
            obstructionHitCount,
        });

        index += 1;
        self.moduleFillSimulationTimer = setTimeout(placeNext, simulationDelayMs);
    };

    placeNext();
}

function getBallastedModuleVertices(module, roofMesh) {
    module.updateWorldMatrix(true, true);
    module.geometry.computeBoundingBox();
    const box = module.geometry.boundingBox;
    const bottomY = box.min.y;
    const localCorners = [
        new THREE.Vector3(box.min.x, bottomY, box.min.z),
        new THREE.Vector3(box.max.x, bottomY, box.min.z),
        new THREE.Vector3(box.max.x, bottomY, box.max.z),
        new THREE.Vector3(box.min.x, bottomY, box.max.z),
    ];

    const verArr = localCorners.map((corner) => {
        const world = corner.clone().applyMatrix4(module.matrixWorld);
        const roofLocal = roofMesh.worldToLocal(world);
        return { x: GetRoundNum(roofLocal.x, 4), z: GetRoundNum(roofLocal.y, 4) };
    });

    const xs = verArr.map((v) => v.x);
    const zs = verArr.map((v) => v.z);
    return {
        verArr,
        x0: Math.min(...xs),
        x1: Math.max(...xs),
        z0: Math.min(...zs),
        z1: Math.max(...zs),
        dX: Math.max(...xs) - Math.min(...xs),
        dZ: Math.max(...zs) - Math.min(...zs),
    };
}

// function getModuleVerInfoForPlacement(module, roofMesh) {
//     if (module.userData?.isBallastedRackType) {
//         return getBallastedModuleVertices(module, roofMesh);
//     }
//     return GetVerArr({ x: module.position.x, z: module.position.y }, module.selSize, module.dir);
// }

function isBallastedModuleOverlapping(module, otherModules) {
    UpdateMeshOBB(module, 0);
    for (const other of otherModules) {
        if (!other || other.moduleId === module.moduleId) continue;
        UpdateMeshOBB(other, 0);
        if (DoesIntersectOBB(module, other)) return true;
    }
    return false;
}

function getRackingModulePlacementStatus(module, roofMesh, self, otherModules, pathwayArr, setbackArr, obstMeshArr) {
    const verInfo = module.userData?.isBallastedRackType ? getBallastedModuleVertices(module, roofMesh) : GetVerArr({x: module.position.x, z: module.position.y}, module.selSize, module.dir, false, roofMesh.oriAng.azimuth);
    module.verInfo = verInfo;
    const status = CanModuleBePlaced(
        verInfo,
        roofMesh.pos2DArr,
        roofMesh.holeArr,
        obstMeshArr,
        roofMesh,
        setbackArr,
        true,
        module,
        otherModules,
        pathwayArr,
        self.wallMeshArr,
        true,
    );
    if (status !== ModulePlacementStatusOk) return status;
    if (isBallastedModuleOverlapping(module, otherModules)) {
        self.modulePlacementStatus = ModulePlacementStatusModule;
        return ModulePlacementStatusModule;
    }
    return ModulePlacementStatusOk;
}

function getModulePlacementStatusForRemoval(module, roofMesh, self, obstArr, setbackArr) {
    const { pos2DArr, holeArr } = roofMesh;
    const { roofGroup, wallMeshArr } = self;
    const { pathwayArr } = GetSetPathArr(roofGroup, roofMesh.roofFaceId);
    const verInfo = getBallastedModuleVertices(module, roofMesh);

    // Module overlap is validated separately via OBB in removeInvalidRackingsPanels.
    return CanModuleBePlaced(
        verInfo,
        pos2DArr,
        holeArr ?? [],
        obstArr,
        roofMesh,
        setbackArr ?? [],
        false,
        module,
        [],
        pathwayArr ?? [],
        wallMeshArr ?? [],
        true,
    );
}

export function removeInvalidRackingsPanels(self, roofMesh, opts = {}) {
    if (!self || !roofMesh) return 0;

    const onlyBallasted = opts.onlyBallasted ?? false;
    const dryRun = opts.dryRun ?? false;

    const { roofGroup, obstGroup, wallMeshArr, moduleMeshArr } = self;
    const { roofFaceId, pos2DArr, holeArr } = roofMesh;
    if (!roofFaceId || !pos2DArr) return 0;

    const obstArr = obstGroup?.children?.map((c) => c.children?.[0]?.children?.[0]?.children?.[0]).filter(Boolean) ?? [];
    const { setbackArr } = GetSetPathArr(roofGroup, roofFaceId);

    const roofModules = getRoofModuleMeshes(roofMesh);
    const invalidModules = [];
    const invalidModuleIds = new Set();

    const isInScope = (m) => (!onlyBallasted || !!m?.userData?.isBallastedRackType);
    const compareModuleIds = (a, b) => {
        const aId = String(a?.moduleId ?? '');
        const bId = String(b?.moduleId ?? '');
        if (aId === bId) return 0;
        return aId > bId ? 1 : -1;
    };

    // Precompute OBB for overlap checks (existing modules can be stale after transforms)
    for (const m of roofModules) {
        if (m) UpdateMeshOBB(m, 0);
    }

    // 1) Placement status checks (roof, setbacks, walls, pitch, obstructions, pathways)
    for (const module of roofModules) {
        if (!isInScope(module)) continue;
        if (!module?.selSize || !module?.dir) continue;

        const status = getModulePlacementStatusForRemoval(
            module,
            roofMesh,
            self,
            obstArr,
            setbackArr ?? [],
        );

        if (status !== ModulePlacementStatusOk) {
            invalidModuleIds.add(module.moduleId);
            invalidModules.push(module);
        }
    }

    // 2) Overlap checks: remove any modules that overlap other modules on the same roof
    // Rules:
    // - If a scoped module overlaps an out-of-scope module, remove the scoped one.
    // - If two scoped modules overlap, remove the one with the "higher" moduleId (stable tie-break).
    for (let i = 0; i < roofModules.length; i++) {
        const a = roofModules[i];
        if (!a || invalidModuleIds.has(a.moduleId)) continue;

        for (let j = i + 1; j < roofModules.length; j++) {
            const b = roofModules[j];
            if (!b || invalidModuleIds.has(b.moduleId)) continue;

            if (!DoesIntersectOBB(a, b)) continue;

            const aScope = isInScope(a);
            const bScope = isInScope(b);

            if (onlyBallasted && (!aScope || !bScope)) continue;

            if (aScope && !bScope) {
                invalidModuleIds.add(a.moduleId);
                invalidModules.push(a);
                break; // 'a' already invalid; move to next 'a'
            }
            if (!aScope && bScope) {
                invalidModuleIds.add(b.moduleId);
                invalidModules.push(b);
                continue;
            }
            if (aScope && bScope) {
                const removeA = compareModuleIds(a, b) > 0;
                const toRemove = removeA ? a : b;
                invalidModuleIds.add(toRemove.moduleId);
                invalidModules.push(toRemove);
                if (removeA) break; // 'a' removed; stop checking it
            }
        }
    }
    
    if (!invalidModules.length) return 0;

    if (dryRun) return invalidModules.length;

    DeleteModule(invalidModules, roofMesh, moduleMeshArr, self, /*type*/ null);

    getRoofModuleMeshes(roofMesh).forEach((module) => {
        if (module) UpdateMeshOBB(module, 0);
    });

    return invalidModules.length;
}


function RemoveModulesUnderDormers(self,roofMesh){

    const modules = getRoofModuleMeshes(roofMesh);

    modules.forEach(module => {

        const raycaster = new THREE.Raycaster();

        const moduleLocalPositon = module.position.clone();

        const moduleCenter =  module.getWorldPosition(moduleLocalPositon);

        const moduleCorners = module.wPosArr

        const allModulePointsToRayTest = [
            ...moduleCorners,
            moduleCenter
        ]

        allModulePointsToRayTest.forEach(corner => {
            const cornerPos = corner.clone();

            raycaster.set(cornerPos, new THREE.Vector3(0, 1, 0));

            const intersects = raycaster.intersectObjects(self.roofMeshArr, false);

            if (intersects.length > 0) {
                roofMesh.remove(module);
            }
        });
    })
}

export function GetModuleXZ(selSize, dir) {
    return {
        dX: dir === 'port' ? selSize.w : selSize.h,
        dZ: dir === 'port' ? selSize.h : selSize.w
    };
}

export function GetVerArr(cPos, selSize, dir) {
    const {x, z} = cPos, verArr = [], {dX, dZ} = GetModuleXZ(selSize, dir);
    [-1, 1].forEach(xDir => {
        [-1, 1].forEach(zDir => {
            verArr.push({x: GetRoundNum(x + xDir * dX / 2, 4), z: GetRoundNum(z + zDir * dZ / 2, 4)})
        });
    });
    const x0 = cPos.x - dX / 2, x1 = cPos.x + dX / 2, z0 = cPos.z - dZ / 2, z1 = cPos.z + dZ / 2;
    return {verArr, x0, x1, z0, z1, dX, dZ};
}

function GetLimitPosValue(posArr) {
    const xArr = [], zArr = [], lastNum = posArr.length - 1;
    posArr.forEach(pos => {
        xArr.push(pos.x);
        zArr.push(pos.z);
    });
    xArr.sort((a, b) => a - b);
    zArr.sort((a, b) => a - b);
    return {minX: xArr[0], maxX: xArr[lastNum], minZ: zArr[0], maxZ: zArr[lastNum]};
}

export function SetMaxFillForRoofs(self, roofId = null) {
    const _perfT0 = perfNow();
    const { roofMeshArr, obstGroup, wallMeshArr } = self;
    const selSize = useViewerStore.getState().activeModule;
    const obstMeshArr = obstGroup.children.map(c => c.children[0].children[0].children[0]).filter(obstMesh=> obstMesh.roofId!==undefined);
    const originalCamZoom = self.camera.zoom;
    const originalCamPos = self.camera.position.clone();
    const originalTarget = self.controls.target.clone();

    // Module count previews should use the same view-aligned corner logic.
    if (self.viewMode === '2d') {
        self.setCameraView('3d');
    }

    // Keep preview counts in sync with the same autofill solver used for placement.
    const orientations = ['port', 'land', 'both'];

    const processRoofMesh = (roofMesh) => {
        if (!roofMesh) return;

        // If roof is disabled or too steep, set counts to zero and exit early
        if (roofMesh.disableModule || isRoofTooSteep(roofMesh.oriAng.tilt)) {
            roofMesh.userData.moduleCount = { port: 0, land: 0, both: 0 };
            return;
        }

        const roofProcT0 = perfNow();
        focusCameraToRoofForFill(self, roofMesh);

        // Initialize the module count object
        roofMesh.userData.moduleCount = {};

        orientations.forEach(dir => {
            // Refresh predicted counts for this building so the 33% rule baseline
            // matches what the actual autofill / default maxfill will use.
            precomputePredictedFillCountsTs(
                getModuleFillingContext(),
                self,
                selSize,
                dir,
                roofMesh.userData?.buildingName
            );
            const moduleArr = computeArrangementWithAutofillGuards(
                self,
                roofMesh,
                selSize,
                dir,
                obstMeshArr,
                wallMeshArr
            );
            roofMesh.userData.moduleCount[dir] = moduleArr.length;
        });
        const roofProcMs = perfNow() - roofProcT0;
        if (isModulePerfLogEnabled() && roofProcMs >= 100) {
            perfLog('SetMaxFillForRoofs roof', roofProcMs, roofMesh.roofFaceId);
        }
    };

    if (roofId === null || roofId === undefined) {
        // Update all roofs
        roofMeshArr.forEach(roofMesh => processRoofMesh(roofMesh));
    } else {
        // Update only specified roof
        const roofMesh = roofMeshArr.find(mesh => mesh.roofFaceId === roofId);
        if (!roofMesh) return;
        processRoofMesh(roofMesh);
    }

    self.camera.zoom = originalCamZoom;
    self.camera.position.copy(originalCamPos);
    self.controls.target.copy(originalTarget);
    self.camera.updateProjectionMatrix();
    self.controls.update();

    refreshContextMenu();
    perfLog('SetMaxFillForRoofs total', perfNow() - _perfT0, roofId == null ? 'all roofs' : `roofId=${roofId}`);
}


//Affects the limit as to how steep the roof can be before modules can no longer be placed on them
const isRoofTooSteep = (tilt) => tilt > 65;

export function SetBuildingModule(self, value) {
    const {preSelRoofId, roofMeshArr} = self;
    if (!preSelRoofId) {
        return;
    }

    const selRoofMesh = roofMeshArr.find(item => item.roofFaceId === preSelRoofId);
    if (!selRoofMesh || !selRoofMesh.userData?.buildingName) {
        return;
    }
    const {userData, disableModule} = selRoofMesh;


    if (disableModule === value
        || (disableModule === undefined && !value) && !useViewerStore.getState().accessoryBuildingList.includes(userData.buildingName)
    ) {
        return;
    }

    if (isRoofTooSteep(selRoofMesh.oriAng.tilt)) return;

    const selRoofArr = roofMeshArr.filter(item => item.userData.buildingName === userData.buildingName);

    if (value) {
        // Setbuilding Accessory
        selRoofArr.forEach(roof => {
            roof.disableModule = true;

            const modules = getRoofModuleMeshes(roof);
            if (modules.length > 0) {
                DeleteModule(modules, roof, self.moduleMeshArr, self, true);
            }

            for (let i = roof.children.length - 1; i >= 0; i--) {
                const child = roof.children[i];
                if (child.meshType === 'pathway') {
                    roof.remove(child);
                    self.meshArr.splice(self.meshArr.findIndex(m => m.uuid === child.uuid), 1);
                    self.pathwayMeshArr.splice(self.pathwayMeshArr.findIndex(m => m.uuid === child.uuid), 1);
                } else if (child.meshType === 'setback') {
                    roof.remove(child);
                }
            }
        });

        useViewerStore.setState({
            accessoryBuildingList: [...useViewerStore.getState().accessoryBuildingList, userData.buildingName],
        });
    } else {
        // Setbuilding Primary

        useViewerStore.setState(state => ({
            accessoryBuildingList: state.accessoryBuildingList.filter(b => b !== userData.buildingName),
        }));

        selRoofArr.forEach(roof => {
            const face = self.roofs[0].faces.find(f => f.roofFaceId === roof.roofFaceId);
            roof.disableModule = false;

            if (roof.userData.roof_size_type === 'normal_face') {
                AddPathwayMesh(face, roof, JSON.parse(JSON.stringify(roof.pos2DArr)), self);
                addModulesToRoofAutoFill(self, roof, null, 'both', true);
            }
        });



        //compare roofId of all items in selfRoofArr and add missing items to Saved Roof Module Layout
        selRoofArr.forEach(roof => {
            // make active all pathways that are green
            const roofChildren = roof.children;
            roofChildren.forEach(child => {
                if (child.meshType === 'pathway') {
                    //check if pathway color is set to green then set pathActive to true
                    const pathwayColor = child.material.color.getHex();
                    if (pathwayColor === 0x00FF00) {
                        child.pathActive = true;
                    }
                }
            });

            const roofFaceId = roof.roofFaceId;
            const oldModeArrInfo = useViewerStore.getState().savedRoofModulesLayout;
            const oldRoof = oldModeArrInfo.find(r => r.roofFaceId === roofFaceId);
            if (!oldRoof) {
                const newRoof = {
                    roofFaceId: roofFaceId,
                    moduleArr: []
                }
                useViewerStore.setState(state => ({
                    savedRoofModulesLayout: [...state.savedRoofModulesLayout, newRoof]
                }));
            }

        });




        siteCalculationState.needsUpdate(ACCESS_MULTIPLIERS);
        siteCalculationState.useCalculations(ACCESS_MULTIPLIERS);
    }
}


export function GetVer3DArr(roofMesh, verArr) {
    const ver3DArr = [];
    verArr.forEach(ver => {
        const cloneTestMesh = testMesh.clone();
        cloneTestMesh.position.set(ver.x, ver.z, 0);
        roofMesh.add(cloneTestMesh);
        const wPos = cloneTestMesh.getWorldPosition(new THREE.Vector3());
        roofMesh.remove(cloneTestMesh);
        ver3DArr.push(wPos);
    });
    return ver3DArr;
}


function CleanPolygon(polygon) {
    return cleanPolygonForFillingTs(polygon);
}

function GetAllowedAreas(pos2DArr, setbackArr, pathwayArr) {
    return getAllowedAreasForFillingTs(getModuleFillingContext(), pos2DArr, setbackArr, pathwayArr);
}

function clearAllowedAreasOverlay(roofMesh) {
    return clearAllowedAreasOverlayTs(getModuleFillingContext(), roofMesh);
}

function applyDebugOverlayVisibility(roofMesh) {
    return applyDebugOverlayVisibilityTs(getModuleFillingContext(), roofMesh);
}

function renderAllowedAreasOverlay(roofMesh, allowedAreas, variant = 'autofill') {
    return renderAllowedAreasOverlayForFillingTs(getModuleFillingContext(), roofMesh, allowedAreas, variant);
}

function SubtractPolygonAreas(areas, polygonToSubtract) {
    const newAreas = [];
    areas.forEach(area => {
        try {
            const difference = PolygonClipping.difference(
                [area.posArr.map(p => [p.x, p.z])],
                [polygonToSubtract.map(p => [p.x, p.z])]
            );
            difference.forEach(diffArea => {
                if (diffArea.length > 0) {
                    newAreas.push({posArr: diffArea[0].map(p => ({x: p[0], z: p[1]}))});
                }
            });
        } catch (error) {
            console.error('PolygonClipping error:', error);
            newAreas.push(area);
        }
    });
    return newAreas;
}

function IsPointInPolygon(point, polygon) {
    return isPointOnPolygonBoundary(point, polygon) || pointInPolygon([point.x, point.z], polygon.map(p => [p.x, p.z]));
}

function getViewAlignedStartingCorner(roofMesh, camera, pos2DArr, requestedCorner) {
    return getViewAlignedStartingCornerTs(getModuleFillingContext(), roofMesh, camera, pos2DArr, requestedCorner);
}

function isPointOnPolygonBoundary(point, polygon, eps = 1e-4) {
    if (!polygon || polygon.length < 2) return false;
    for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        const abx = b.x - a.x;
        const abz = b.z - a.z;
        const apx = point.x - a.x;
        const apz = point.z - a.z;
        const cross = Math.abs(abx * apz - abz * apx);
        if (cross > eps) continue;
        const dot = apx * abx + apz * abz;
        if (dot < -eps) continue;
        const lenSq = abx * abx + abz * abz;
        if (dot - lenSq > eps) continue;
        return true;
    }
    return false;
}

function GetModuleFaceArr(
    roofMesh,
    pos2DArr,
    holeArr,
    obstMeshArr,
    selSize,
    preDir,
    rotX,
    setbackArr,
    pathwayArr,
    flagPath,
    wallMeshArr,
    startingCorner,
    maxCount = Infinity,
) {
    const sceneViewer = useViewerStore.getState().sceneViewer;
    const obstructionDebugIds = new Set();
    let obstructionHitCount = 0;
    let lastBlockedObstructionId = null;
    const registerObstructionHit = () => {
        obstructionHitCount += 1;
        if (lastBlockedObstructionId) obstructionDebugIds.add(String(lastBlockedObstructionId));
    };

    let mPos2DArr = [];
    const doPlannedModulesOverlap = (candidateX, candidateZ, candidateDir, plannedModules) => {
        const candidateVer = GetVerArr({ x: candidateX, z: candidateZ }, selSize, candidateDir);
        const eps = 1e-6;
        return plannedModules.some((item) => {
            const existingVer = GetVerArr({ x: item.x, z: item.z }, selSize, item.dir);
            const separatedX = candidateVer.x1 <= existingVer.x0 + eps || candidateVer.x0 >= existingVer.x1 - eps;
            const separatedZ = candidateVer.z1 <= existingVer.z0 + eps || candidateVer.z0 >= existingVer.z1 - eps;
            return !(separatedX || separatedZ);
        });
    };

    // If ballasted rack is active, force generation to only landscape modules
    const isBallastedRackTypeModuleActive = useViewerStore.getState().isBallastedRackTypeModuleActive;
    // const effectivePreDir = isBallastedRackTypeModuleActive ? 'land' : preDir;
    const effectivePreDir = preDir;


    const moduleDir = effectivePreDir === 'land' ? 'land' : 'port';
    const startingCornerForFill = getViewAlignedStartingCorner(roofMesh, sceneViewer?.camera, pos2DArr, startingCorner);

    const allowedAreas = GetAllowedAreas(pos2DArr, setbackArr, pathwayArr);
    if (isOneDrawDebugMode() && typeof window !== 'undefined') {
        const allowedAreasSummary = allowedAreas.map((area, idx) => {
            const { minX, maxX, minZ, maxZ } = GetLimitPosValue(area.posArr);
            return {
                idx,
                points: area.posArr.length,
                minX: GetRoundNum(minX, 4),
                maxX: GetRoundNum(maxX, 4),
                minZ: GetRoundNum(minZ, 4),
                maxZ: GetRoundNum(maxZ, 4),
                vertices: area.posArr.slice(0, 10).map((p) => ({ x: GetRoundNum(p.x, 4), z: GetRoundNum(p.z, 4) })),
            };
        });
        window.dispatchEvent(new CustomEvent('module-fill-debugger-info', {
            detail: {
                roofFaceId: roofMesh?.roofFaceId,
                allowedAreasCount: allowedAreas.length,
                allowedAreasSummary,
            },
        }));
    }

    const spaceSingle = space;
    const activeBallastRackDirection = useViewerStore.getState().activeBallastRackDirection;

    let horizontalRowSpace = spaceSingle; // spacing along X axis between rows/columns
    let verticalRowSpace = spaceSingle;   // spacing along Z axis between rows/columns

    if (isBallastedRackTypeModuleActive) {
        const isParallelRack = activeBallastRackDirection === 'parallel' || activeBallastRackDirection === 'horizontal';
        if (isParallelRack) {
            verticalRowSpace = GetRoundNum(18.875 * inch2m, 4);
            horizontalRowSpace = spaceSingle;
        } else if (activeBallastRackDirection === 'vertical') {
            verticalRowSpace = spaceSingle;
            horizontalRowSpace = GetRoundNum(18.875 * inch2m, 4);
        }
    }

    const dX = moduleDir === ModuleOrientation.Landscape ? selSize.h : selSize.w;
    const dZ = moduleDir === ModuleOrientation.Landscape ? selSize.w : selSize.h;
    const gapLand = horizontalRowSpace + selSize.h / 2;

        const canPlaceModule = (verInfo, roundX, roundZ, dir) => {
            lastBlockedObstructionId = null;
            const centerPoint = { x: roundX, z: roundZ };
            const containingAllowedArea = allowedAreas.find((area) => IsPointInPolygon(centerPoint, area.posArr));
            if (!containingAllowedArea) return ModulePlacementStatusRoofShape;

            // Maxfill should respect the actual allowed-area boundary, not only center inclusion.
            // Reject candidates whose corners cross outside the containing allowed polygon.
            const isFootprintInsideAllowedArea = verInfo.verArr.every((pos) => IsPointInPolygon(pos, containingAllowedArea.posArr));
            if (!isFootprintInsideAllowedArea) return ModulePlacementStatusRoofShape;

            const module = CreateModuleMesh({ x: roundX, z: roundZ, selSize, dir: dir });
            roofMesh.add(module);

            module.getWorldPosition(new THREE.Vector3());

            const padX = Math.max(0, horizontalRowSpace - spaceSingle);
            const padZ = Math.max(0, verticalRowSpace - spaceSingle);

            const paddedVerInfo = { ...verInfo };
            if (typeof paddedVerInfo.dX === 'number') paddedVerInfo.dX = paddedVerInfo.dX + padX;
            if (typeof paddedVerInfo.dZ === 'number') paddedVerInfo.dZ = paddedVerInfo.dZ + padZ;

            if (typeof paddedVerInfo.x0 === 'number' && typeof paddedVerInfo.x1 === 'number') {
                paddedVerInfo.x0 = paddedVerInfo.x0 - padX / 2;
                paddedVerInfo.x1 = paddedVerInfo.x1 + padX / 2;
            }

            if (typeof paddedVerInfo.z0 === 'number' && typeof paddedVerInfo.z1 === 'number') {
                paddedVerInfo.z0 = paddedVerInfo.z0 - padZ / 2;
                paddedVerInfo.z1 = paddedVerInfo.z1 + padZ / 2;
            }

            const obstructionInfo = { lastBlockedObstructionId: null };
            const flag = CanModuleBePlaced(
                paddedVerInfo,
                containingAllowedArea.posArr,
                holeArr,
                obstMeshArr,
                roofMesh,
                [],
                false,
                module,
                [],
                [],
                [],
                false,
                obstructionInfo
            );
            lastBlockedObstructionId = obstructionInfo.lastBlockedObstructionId;

            RemoveModulesUnderDormers(sceneViewer, roofMesh);
            roofMesh.remove(module);
            return flag;
        };

    mPos2DArr = generateModulePlacements({
        areas: allowedAreas,
        startingCorner: startingCornerForFill,
        effectivePreDir,
        moduleDir,
        selSize,
        horizontalRowSpace,
        verticalRowSpace,
        moduleDx: dX,
        moduleDz: dZ,
        edgeBuffer: inch4,
        modulePlacementStatusOk: ModulePlacementStatusOk,
        modulePlacementStatusObstruction: ModulePlacementStatusObstruction,
        getLimitPosValue: GetLimitPosValue,
        getRoundNum: GetRoundNum,
        isPointInPolygon: IsPointInPolygon,
        getVerArr: GetVerArr,
        canPlaceModule,
        onPlacementRejected: (status) => {
            if (status === ModulePlacementStatusObstruction) {
                registerObstructionHit();
            }
        },
        getOverPathIds: (verInfo) => GetOverPathIds(verInfo, pathwayArr),
        getIdStr: GetIdStr,
    });

    const roofAzimuth = roofMesh.oriAng?.azimuth ?? 0;
    if (isBallastedRackTypeModuleActive && effectivePreDir === 'land') {
        mPos2DArr = mPos2DArr.map((item) => ({
            ...item,
            isBallastedRackType: true,
            ballastRackDirection: activeBallastRackDirection,
            ballastedPitch: roofMesh.userData.ballastedPitch ?? sceneViewer?.ballastAngle ?? 0,
            ballastedAzimuth: roofMesh.userData.ballastedAzimuth ?? sceneViewer?.ballastAzimuth ?? roofAzimuth,
        }));
    }

    if (Number.isFinite(maxCount) && mPos2DArr.length > maxCount) {
        mPos2DArr = mPos2DArr.slice(0, maxCount);
    }

    if (effectivePreDir === 'both') {
        const allowedLandAreas = allowedAreas.map(area => {
            const { minX, maxX, minZ, maxZ } = GetLimitPosValue(area.posArr);
            const realMinZ = minZ + (verticalRowSpace + dZ / 2 + 0.005);
            const realMaxZ = maxZ - (verticalRowSpace + dZ / 2 + 0.005);
            const realMinX = minX + (horizontalRowSpace + dX / 2);
            const realMaxX = maxX - (horizontalRowSpace + dX / 2);

            const isTopStart = startingCornerForFill === 'topLeft' || startingCornerForFill === 'topRight';
            const adjustedLastZ = (isTopStart ? realMaxZ : realMinZ) + (dZ / -2 + selSize.w / 2) * (isTopStart ? -1 : 1);

            return {
                posArr: area.posArr,
                zOverride: adjustedLastZ,
                minX: realMinX,
                maxX: realMaxX,
            };
        });

        allowedLandAreas.forEach((entry) => {
            const { posArr, zOverride, minX, maxX } = entry;
            const xDir = (startingCornerForFill === 'topRight' || startingCornerForFill === 'bottomRight') ? -1 : 1;
            let adjustedLastX = xDir === 1 ? minX + gapLand : maxX - gapLand;

            while (xDir > 0 ? adjustedLastX <= maxX - gapLand : adjustedLastX >= minX + gapLand) {
                const roundX = GetRoundNum(adjustedLastX, 4);
                const roundZ = GetRoundNum(zOverride, 4);
                const point = { x: roundX, z: roundZ };

                if (!IsPointInPolygon(point, posArr)) {
                    adjustedLastX += (selSize.h + horizontalRowSpace) * xDir;
                    continue;
                }

                const verInfo = GetVerArr(point, selSize, 'land');
                if (doPlannedModulesOverlap(roundX, roundZ, 'land', mPos2DArr)) {
                    adjustedLastX += (selSize.h + horizontalRowSpace) * xDir;
                    continue;
                }
                const flag = canPlaceModule(verInfo, roundX, roundZ, 'land');
                adjustedLastX += (selSize.h + horizontalRowSpace) * xDir;
                if (flag === ModulePlacementStatusObstruction) {
                    registerObstructionHit();
                }
                if (flag !== ModulePlacementStatusOk) continue;
                mPos2DArr.push({
                    x: roundX,
                    z: roundZ,
                    selSize,
                    dir: 'land',
                    moduleId: GetIdStr(),
                    overPathIds: GetOverPathIds(verInfo, pathwayArr),
                    isBallastedRackType: isBallastedRackTypeModuleActive,
                    ballastRackDirection: activeBallastRackDirection,
                    ballastedPitch: roofMesh.userData.ballastedPitch ?? 0,
                    ballastedAzimuth: roofMesh.userData.ballastedAzimuth ?? roofAzimuth,
                });
                if (mPos2DArr.length >= maxCount) return mPos2DArr;
            }
        });
    }

    // Filter out landscape modules at the bottom of the roof if preDir is not 'land'
    if (preDir !== 'land') {
        const bottomRoofZ = Math.min(...mPos2DArr.map(module => module.z));
        const filtered = mPos2DArr.filter(module => !(module.dir === 'land' && module.z === bottomRoofZ));
        roofMesh.userData.fillDebugObstructionInfo = {
            obstructionIds: Array.from(obstructionDebugIds),
            obstructionHitCount,
        };
        return filtered;
    }

    roofMesh.userData.fillDebugObstructionInfo = {
        obstructionIds: Array.from(obstructionDebugIds),
        obstructionHitCount,
    };
    return mPos2DArr;
}



function GetOverPathIds(verInfo, pathwayArr) {
    const {verArr} = verInfo, lineArr = [verArr[0], verArr[1], verArr[3], verArr[2]];
    const overPathIds = [];
    verArr.forEach(pos => {
        pathwayArr.forEach(pathway => {
            if (!overPathIds.includes(pathway.pathwayId) && CheckInsidePos(pathway.posArr, pos)) overPathIds.push(pathway.pathwayId);
        });
    });
    pathwayArr.forEach(pathway => {
        pathway.posArr.forEach(pos => {
            if (!overPathIds.includes(pathway.pathwayId) && CheckInsidePos(lineArr, pos)) overPathIds.push(pathway.pathwayId);
        });
    });
    return overPathIds;
}

// Check if module position is valid -
function CheckModulePos(verInfo, pos2DArr, holeArr, obstArr, setbackArr, flagPath, module, wallMeshArr, roofMesh, isUserAction = false, obstructionInfoOut = null) {
    const {verArr, x0, x1, z0, z1} = verInfo, lineArr = [verArr[0], verArr[1], verArr[3], verArr[2]],
        insideDelta = inch4, gap = inch2m - 0.0001;
        const safe = (n) => (typeof n === 'number' && !isNaN(n)) ? n : 0;
        const x0s = safe(x0), x1s = safe(x1), z0s = safe(z0), z1s = safe(z1);

        // if ground mount, don't apply the insideDelta shrink; allow a small minimum gap
        const isGround = isGroundMount(roofMesh);
        const insideDeltaAdj = isGround ? 0 : insideDelta;
        // For autofill, allow true edge-touching placement against allowed areas.
        // Keep stricter boundary buffer for direct user/manual placement only.
        const effectiveInsideDelta = isUserAction ? insideDeltaAdj : 0;
        const effectiveGap = isUserAction ? gap : 0;

        const bufferVerArr = [
            { x: x0s - effectiveInsideDelta + effectiveGap, z: z0s - effectiveInsideDelta + effectiveGap },
            { x: x1s + effectiveInsideDelta - effectiveGap, z: z0s - effectiveInsideDelta + effectiveGap },
            { x: x1s + effectiveInsideDelta - effectiveGap, z: z1s + effectiveInsideDelta - effectiveGap },
            { x: x0s - effectiveInsideDelta + effectiveGap, z: z1s + effectiveInsideDelta - effectiveGap },
        ];
    for (const pos of bufferVerArr) {
        if (!CheckInsidePos(pos2DArr, pos)) return ModulePlacementStatusRoofShape;
    }

    // For manual placement we keep this concavity guard.
    // For autofill on computed allowed areas, this can incorrectly reject edge-maxfill candidates.
    if (isUserAction) {
        for (const pos of pos2DArr) {
            if (CheckInsidePos(bufferVerArr, pos) && !isPointOnPolygonBoundary(pos, lineArr)) {
                return ModulePlacementStatusRoofShape;
            }
        }
    }

    for (const pos of bufferVerArr) {
        for (const holePosArr of holeArr) {
            if (CheckInsidePos(holePosArr, pos)) return ModulePlacementStatusRoofShape;
        }
    }
    const addSetbackCheckToUser = !useViewerStore.getState().isUserHaveCapabilityToAddPanelsPastSetbacks;
    

    if ( (!isUserAction && flagPath) || (isUserAction && addSetbackCheckToUser && !isGivenRoofPartOfDetachedStructure(roofMesh))  ) {

        for (const pos of verArr) {
            for (const setback of setbackArr) {
                if(setback.isMarkedInactive) return
                if (CheckInsidePos(setback.posArr, pos)) return ModulePlacementStatusSetback;
            }
        }

        for (const setback of setbackArr) {
            for (const pos of setback.posArr) {
                if(setback.isMarkedInactive) return
                if (CheckInsidePos(lineArr, pos)) return ModulePlacementStatusSetback;
            }
        }
    }


    let isBallastedRackTypeModuleActive = useViewerStore.getState().isBallastedRackTypeModuleActive;

    if(module.userData.isBallastedRackType) {
        isBallastedRackTypeModuleActive = true;
    }

    if(isBallastedRackTypeModuleActive) {
        //if roof pitch is greater than 5 degrees then flag
        if(roofMesh.userData.pitchOverride > 5) {
            return ModulePlacementStatusRoofPitchBallast;
        }
    }
    if(!isBallastedRackTypeModuleActive) {
        if(roofMesh.userData.pitchOverride < 3 && !isGroundMount(roofMesh)) {
            return ModulePlacementStatusRoofPitch;
        }
    }



    if (module) {
        UpdateMeshOBB(module, 0);
        const collisionDebugEnabled = !!getOneDrawDebuggerOptions().fillDebugger.showModuleCollisionObb;
        const sceneViewer = collisionDebugEnabled ? useViewerStore.getState().sceneViewer : null;
        if (collisionDebugEnabled && sceneViewer) {
            ClearDebugDraw(sceneViewer, 'module-collision');
        }

        for (const obstItem of obstArr) {

            //if(obstMesh.delStatus) then ignore the obstruction
            if (obstItem.delStatus) continue;

            UpdateMeshOBB(obstItem, inch4);
            const hitObstruction = DoesIntersectOBB(module, obstItem);
            if (collisionDebugEnabled && sceneViewer) {
                DebugDrawManager.execute(sceneViewer, 'drawModuleCollisionObb', {
                    object: obstItem,
                    isHit: hitObstruction,
                    group: 'module-collision',
                });
            }

            if (hitObstruction) {
                if (obstructionInfoOut) {
                    obstructionInfoOut.lastBlockedObstructionId =
                        obstItem?.obstId || obstItem?.obstructionId || obstItem?.name || obstItem?.uuid || null;
                }
                return ModulePlacementStatusObstruction;
            }
        }

        if(!isGroundMount(roofMesh)) {
            for (const wall of wallMeshArr) {
                UpdateWallMeshOBB(wall, 4 * inch2m);

                if (DoesIntersectOBB(module, wall)) {
                    return ModulePlacementStatusWall;
                }
            }
        }

    }
    return ModulePlacementStatusOk;
}

function GetNearModules(moduleArr, verInfo, moduleId, nearDis = snapDis, type) {
    return moduleArr.filter(item => {
        const flagTransCheck = type === 'overCheck' || (type === 'snapPos' && item.select !== true);
        return item.moduleId !== moduleId && flagTransCheck &&
            verInfo.x0 < item.verInfo.x1 + nearDis &&
            verInfo.x1 > item.verInfo.x0 - nearDis &&
            verInfo.z0 < item.verInfo.z1 + nearDis &&
            verInfo.z1 > item.verInfo.z0 - nearDis;
    });
}

function CheckModuleOver(selItem, moduleArr) {
    // UpdateMeshOBB(selItem, 0);

    // for (const module of moduleArr) {
    //     UpdateMeshOBB(module, inch2m);

    //     if (DoesIntersectOBB(selItem, module)) {
    //         return false;
    //     }
    // }

    // return true;

    const {moduleId, verInfo, position} = selItem,
        selVerArr = verInfo.verArr,
        verMin = selVerArr[0],
        verMax = selVerArr[3];
    const nearModules = GetNearModules(moduleArr, verInfo, moduleId, undefined, 'overCheck');
    const isBallasted = selItem.userData?.isBallastedRackType || useViewerStore.getState().isBallastedRackTypeModuleActive;
    var flag = true;
    nearModules.forEach(item => {
        if (flag === false) return;
        const {x0, x1, z0, z1} = item.verInfo;
        // If ballasted, use a more lenient check to allow for expanded bounding boxes touching or slightly overlapping
        const epsilon = isBallasted ? 0.2 : 0.01;
        const disNear = Math.abs(position.x - item.position.x) + Math.abs(position.y - item.position.y);
        // Only enforce center-to-center distance if not ballasted or if extremely close
        if (!isBallasted && disNear < 0.6) flag = false;
        selVerArr.forEach(ver => {
            if ((ver.x >= x0) && (ver.x <= x1) && (ver.z >= z0) && (ver.z <= z1)) flag = false;
            if ((ver.x > x0 + epsilon) && (ver.x < x1 - epsilon) && (ver.z > z0 + epsilon) && (ver.z < z1 - epsilon)) flag = false;
        });
        item.verInfo.verArr.forEach(ver => {
            if ((ver.x >= verMin.x) && (ver.x <= verMax.x) && (ver.z >= verMin.z) && (ver.z <= verMax.z)) flag = false;
            if ((ver.x > verMin.x + epsilon) && (ver.x < verMax.x - epsilon) && (ver.z > verMin.z + epsilon) && (ver.z < verMax.z - epsilon)) flag = false;
        });
    });
    return flag;
}

function snapshotModuleMoveStart(module, roofMesh) {
    if (!module) return;
    const {x, y, z} = module.position;
    module.oriPos = {x, y, z};
    module.moveStartRotation = {x: module.rotation.x, y: module.rotation.y, z: module.rotation.z};
    const layoutModule = roofMesh?.moduleArr?.find((m) => m.moduleId === module.moduleId);
    if (layoutModule) {
        module.moveStartLayout = {x: layoutModule.x, z: layoutModule.z};
    }
}

function revertModulesToMoveStart(selModules, selRoof, roofId) {
    const modeArrInfo = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout));
    const roofLayout = modeArrInfo.find((r) => r.roofFaceId === roofId);

    selModules.forEach((module) => {
        const {oriPos, moveStartRotation} = module;
        if (oriPos) {
            module.position.set(oriPos.x, oriPos.y, oriPos.z);
        }
        if (moveStartRotation) {
            module.rotation.set(moveStartRotation.x, moveStartRotation.y, moveStartRotation.z);
        }
        module.material.color.setHex(selModuleCol);
        module.flag = true;
        module.visible = true;
        if (selRoof && !selRoof.children.includes(module)) {
            selRoof.add(module);
        }
        if (module.userData?.isBallastedRackType && selRoof) {
            module.verInfo = getBallastedModuleVertices(module, selRoof);
            UpdateMeshOBB(module, 0);
        } else if (selRoof) {
            module.verInfo = GetVerArr(
                {x: module.position.x, z: module.position.y},
                module.selSize,
                module.dir,
                false,
                selRoof.oriAng.azimuth,
            );
        }
        if (roofLayout && module.moveStartLayout) {
            const layoutEntry = roofLayout.moduleArr?.find((m) => m.moduleId === module.moduleId);
            if (layoutEntry) {
                layoutEntry.x = module.moveStartLayout.x;
                layoutEntry.z = module.moveStartLayout.z;
            }
        }
    });

    if (roofLayout) {
        useViewerStore.setState({savedRoofModulesLayout: modeArrInfo});
    }
}

export function SetModuleOriPos(self, moduleMeshArr) {
    const selModules = GetSelModules(moduleMeshArr);
    if (!selModules || !selModules.length) return false;
    const {roofId} = selModules[0];
    const selRoof = self.roofMeshArr.find((roofMesh) => roofMesh.roofFaceId === roofId);

    if (!selRoof) {
        revertModulesToMoveStart(selModules, null, roofId);
        self.modulePlacementStatus = ModulePlacementStatusOk;
        return false;
    }

    const obstMeshArr = self.obstGroup.children.map(c => c.children[0].children[0].children[0]);
    const {pathwayArr, setbackArr} = GetSetPathArr(self.roofGroup, roofId);
    const otherModules = selRoof.children.filter(item => item.moduleId !== undefined && !item.select);

    let invalidPlacementStatus = null;
    for (const module of selModules) {
        let status = ModulePlacementStatusOk;
        if (module.userData?.isBallastedRackType) {
            status = getRackingModulePlacementStatus(
                module,
                selRoof,
                self,
                otherModules,
                pathwayArr,
                setbackArr,
                obstMeshArr,
            );
        } else {
            const verInfo = GetVerArr(
                {x: module.position.x, z: module.position.y},
                module.selSize,
                module.dir,
                false,
                selRoof.oriAng.azimuth,
            );
            module.verInfo = verInfo;
            status = CanModuleBePlaced(
                verInfo,
                selRoof.pos2DArr,
                selRoof.holeArr ?? [],
                obstMeshArr,
                selRoof,
                setbackArr,
                true,
                module,
                otherModules,
                pathwayArr,
                self.wallMeshArr,
                true,
            );
        }
        if (status !== ModulePlacementStatusOk) {
            invalidPlacementStatus = status;
            module.flag = false;
            break;
        }
        module.flag = true;
    }

    const oldModeArrInfo = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout)); //Added for undo/redo position change
    if (!invalidPlacementStatus) {
        // deep copy
        const modeArrInfo = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout));
        const oldRoof = modeArrInfo.find(r => r.roofFaceId === roofId);

        selModules.forEach(selModule => {
            const {moduleId} = selModule;
            const verInfo = selModule.userData?.isBallastedRackType
                ? getBallastedModuleVertices(selModule, selRoof)
                : selModule.verInfo;
            selModule.verInfo = verInfo;
            const {x0, x1, z0, z1} = verInfo;
            const xM = GetRoundNum((x0 + x1) / 2, 4), zM = GetRoundNum((z0 + z1) / 2, 4);
            if(!oldRoof || !oldRoof.moduleArr) return;
            const oldModule = oldRoof.moduleArr.find(oldMode => oldMode.moduleId === moduleId);
            if (oldModule) {
                oldModule.x = xM;
                oldModule.z = zM;
            }

            const selMod = selRoof.moduleArr.find(m => m.moduleId === moduleId);
            if (selMod) {
                selMod.x = xM;
                selMod.z = zM;
            }
        });

        siteCalculationState.needsUpdate(MODULE_GRAPH);
        siteCalculationState.useCalculations(MODULE_GRAPH);

        useViewerStore.setState({savedRoofModulesLayout: modeArrInfo});
        self.addStep(EditAction.ModuleLayout, 1, JSON.stringify(oldModeArrInfo), JSON.stringify(modeArrInfo));//Updated for undo/redo position change
        return true;
    }

    revertModulesToMoveStart(selModules, selRoof, roofId);
    self.modulePlacementStatus = invalidPlacementStatus ?? ModulePlacementStatusOk;
    return false;
}

export function AddIndividualMode(shapeMesh, item, visible, moduleMeshArr, meshArr, showLabel, isLoadingSavedModule = false) {
    const {overPathIds} = item;
    // const sceneViewer = useViewerStore.getState().sceneViewer;

    const moduleMesh = CreateModuleMesh(item, shapeMesh.oriAng.azimuth);


    moduleMesh.visible = visible;


    let isBallastedRackTypeModuleActive = item.isBallastedRackType;
    let activeBallastRackDirection = item.ballastRackDirection ?? shapeMesh.userData.ballastRackDirection ?? 'horizontal'; //useViewerStore.getState().activeBallastRackDirection;
    let ballastedRackModuleAngle = item.ballastedPitch ?? shapeMesh.userData.ballastedPitch ?? 10; //useViewerStore.getState().ballastedRackModuleAngle;
    let ballastedRackModuleAzimuth = item.ballastedAzimuth ?? shapeMesh.userData.ballastedAzimuth ?? shapeMesh.oriAng.azimuth;

    // if(item.isBallastedRackType){ // isLoadingSavedModule && 
    //     moduleMesh.userData.isBallastedRackType = true;
    //     activeBallastRackDirection = item.ballastRackDirection || activeBallastRackDirection;
    //     // isBallastedRackTypeModuleActive = true;
    //     // moduleMesh.userData.ballastAngle = item?.ballastAngle || ballastedRackModuleAngle;
    //     ballastedRackModuleAngle = item.ballastedPitch !== undefined ? item.ballastedPitch : ballastedRackModuleAngle;
    //     ballastedRackModuleAzimuth = item.ballastedAzimuth !== undefined ? item.ballastedAzimuth : ballastedRackModuleAzimuth;
    //     isBallastedRackTypeModuleActive = true;
    // }

    if(isBallastedRackTypeModuleActive){

        // Removed shared geometry translation to prevent 'breaking' all modules in the scene.
        // Rotation and positioning logic below handles the pivot offset.
        const size = new THREE.Vector3();
        new THREE.Box3().setFromObject(moduleMesh).getSize(size);

        // Shift geometry so origin is at bottom face
        moduleMesh.geometry.translate(0, 0, -moduleMesh.geometry.parameters.depth / 2);
        moduleMesh.geometry.translate(0, 0, size.z / 2);
        const normalizedBallastedAngle = ballastedRackModuleAngle - shapeMesh.oriAng.tilt;

        const tiltRad = -normalizedBallastedAngle * Math.PI / 180;
        const tiltDim = item.dir === 'port' ? item.selSize.h : item.selSize.w;

        // if(activeBallastRackDirection === 'horizontal') {
        //     // moduleMesh.rotateX(-normalizedBallastedAngle * Math.PI / 180);
        //     moduleMesh.rotateX(tiltRad);
        //     // move module in z axis to be flush with roof surface based on rotation
        //     // moduleMesh.position.z -= Math.sin(normalizedBallastedAngle * Math.PI / 180) * (item.selSize.w / 2);
        //     moduleMesh.position.z = -Math.sin(Math.abs(tiltRad)) * (tiltDim / 2);
        // } else if (activeBallastRackDirection === 'vertical') { 
        //     moduleMesh.rotateX(tiltRad); // Pivot around X even for portrait
        //     moduleMesh.position.z = -Math.sin(Math.abs(tiltRad)) * (tiltDim / 2);
        // } else if (activeBallastRackDirection === 'custom') {
        //     const roofAzimuth = shapeMesh?.oriAng?.azimuth ?? 0;
        //     const azDiff = ballastedRackModuleAzimuth - roofAzimuth;

        //     moduleMesh.rotation.z = -azDiff * Math.PI / 180;
        //     moduleMesh.rotateX(tiltRad); // Rotate around local X after Z rotation
        //     moduleMesh.position.z = -Math.sin(Math.abs(tiltRad)) * (tiltDim / 2);
        // }

        const roofAzimuth = shapeMesh?.oriAng?.azimuth ?? 0;
        const azDiff = roofAzimuth - ballastedRackModuleAzimuth;

        moduleMesh.rotation.z = -azDiff * Math.PI / 180;
        moduleMesh.rotateX(tiltRad); // Rotate around local X after Z rotation
        moduleMesh.position.z = -Math.sin(Math.abs(tiltRad)) * (tiltDim / 2);
        // else {
        //     moduleMesh.rotateY(-normalizedBallastedAngle * Math.PI / 180);
        //     //move module in z axis to be flush with roof surface based on rotation
        //     moduleMesh.position.z -= Math.sin(normalizedBallastedAngle * Math.PI / 180) * (item.selSize.w / 2);
        //     moduleMesh.userData.ballastRackDirection = 'vertical';
        //     moduleMesh.userData.ballastAngle = ballastedRackModuleAngle;
        // }

        moduleMesh.userData.isBallastedRackType = true;
        moduleMesh.userData.isBallastFlipped = false;
        moduleMesh.userData.ballastRackDirection = activeBallastRackDirection;
        moduleMesh.userData.ballastedPitch = ballastedRackModuleAngle;
        moduleMesh.userData.ballastedAzimuth = ballastedRackModuleAzimuth;
    }
    else {
        moduleMesh.userData.isBallastedRackType = false;
    }


    if (overPathIds) {
        moduleMesh.overPathIds = [...overPathIds];
    }
    moduleMesh.roofId = shapeMesh.roofFaceId;

    moduleMesh.verInfo.verArr.forEach(ver => {
        const localPos = new THREE.Vector3(ver.x, ver.z, 0);
        const worldPos = shapeMesh.localToWorld(localPos);
        moduleMesh.wPosArr.push(worldPos);
    });


    // add edge line array
    const cornerArr = [];
    [0, 1, 3, 2].forEach(nodeIdx => {
        cornerArr.push(moduleMesh.wPosArr[nodeIdx]);
    });
    moduleMesh.edgeLineArr = [];
    cornerArr.forEach((corner, idx) => {
        moduleMesh.edgeLineArr.push({start: corner, end: cornerArr[(idx + 1) % 4]});
    });

    // Creates module label
    const moduleLabel = createLabel('', '#fff', new THREE.Vector3(0, 0, 0));
    moduleLabel.visible = showLabel;
    moduleLabel.meshType = 'moduleLabel';
    moduleMesh.add(moduleLabel);

    //Create a module ballast angle label if it's a ballasted rack type module
    if(isBallastedRackTypeModuleActive){
        const isAngleLabelVisible = useViewerStore.getState().isBallastedAngleLabelVisible;
        const ballastAngleLabel = createLabel(formatBallastRackingAngleLabel(ballastedRackModuleAngle, ballastedRackModuleAzimuth), '#ffffff', new THREE.Vector3(0, -0.5, 0));
        ballastAngleLabel.visible = isAngleLabelVisible;
        ballastAngleLabel.name = 'ballastAngleLabel';
        if (ballastAngleLabel.element) {
            ballastAngleLabel.element.style.display = isAngleLabelVisible ? '' : 'none';
        }
        moduleMesh.add(ballastAngleLabel);
    }

    shapeMesh.add(moduleMesh);
    moduleMeshArr.push(moduleMesh);

    if(isLoadingSavedModule && item.isBallastedRackType){
        moduleMesh.userData.isBallastFlipped = item?.isBallastFlipped;

        if(item?.isBallastFlipped){
            flipBallastTypeModule(useViewerStore.getState().sceneViewer, moduleMesh);
        }
    }
    const isContainingBallastedRacking = moduleMeshArr.some(module => module.userData.isBallastedRackType);
    if(moduleMeshArr.length > 0 && isContainingBallastedRacking) check25PercentRuleForBallastedRacking(moduleMeshArr);


    meshArr.push(moduleMesh);

    siteCalculationState.needsUpdate(MODULE_GRAPH);
    siteCalculationState.useCalculations(MODULE_GRAPH);
}


export function updateBallastedRackAzimuthForSelectedRoofs(self, roofMesh, newAzimuth, direction, options = {}) {
    if (!roofMesh) return;

    self.moduleDir = ModuleOrientation.Landscape;
    self.ballastAzimuth = newAzimuth;
    self.ballastRackDirection = direction;

    const roofAzimuth = roofMesh?.oriAng?.azimuth ?? 0;
    const azDiff = roofAzimuth - newAzimuth;
    const ballastedModules = self.moduleMeshArr.filter(
        (module) => module.roofId === roofMesh.roofFaceId && module.userData?.isBallastedRackType,
    );

    ballastedModules.forEach((module) => {
        const ballastedPitch = module.userData.ballastedPitch ?? self.ballastAngle ?? roofMesh.userData.ballastedPitch ?? 10;
        let normalizedAngle = ballastedPitch - roofMesh.oriAng.tilt;
        if (ballastedPitch === 0) normalizedAngle = 0;
        const tiltRad = -normalizedAngle * Math.PI / 180;
        const tiltDim = module.userData?.dir === 'port' ? module.selSize.h : module.selSize.w;

        // Match AddIndividualMode: azimuth on Z, tilt on local X, z offset for roof flush.
        module.rotation.set(0, 0, 0);
        module.rotation.z = -azDiff * Math.PI / 180;
        module.rotateX(tiltRad);
        module.position.z = -Math.sin(Math.abs(tiltRad)) * (tiltDim / 2);

        module.userData.ballastedAzimuth = newAzimuth;
        module.userData.ballastRackDirection = direction;
        module.userData.ballastedPitch = ballastedPitch;
        updateBallastAngleLabel(module);
        UpdateMeshOBB(module, 0);
    });

    roofMesh.userData.ballastedAzimuth = newAzimuth;
    roofMesh.userData.ballastRackDirection = direction;

    const newLayout = cloneSavedRoofModulesLayout();
    newLayout.forEach((roofInfo) => {
        if (roofInfo.roofFaceId !== roofMesh.roofFaceId) return;
        roofInfo.moduleArr?.forEach((moduleInfo) => {
            if (!moduleInfo.isBallastedRackType) return;
            moduleInfo.ballastedAzimuth = newAzimuth;
            moduleInfo.ballastRackDirection = direction;
        });
    });

    const syncedLayout = syncBallastedModulesToLayout(self, newLayout);
    addModuleLayoutStep(self, useViewerStore.getState().savedRoofModulesLayout, syncedLayout);
}

export function updateBallastedRackAngle(moduleMesh, shapeMesh, newBallastAngle) {

    if (!moduleMesh.userData.isBallastedRackType) return;
    if (!Number.isFinite(newBallastAngle) || newBallastAngle < MIN_BALLAST_RACKING_ANGLE) return;


    // const activeDirection = moduleMesh.userData.ballastRackDirection || 'horizontal';


    let normalizedAngle = newBallastAngle - shapeMesh.oriAng.tilt;

    if(newBallastAngle === 0){
        normalizedAngle = 0;
    }
    const tiltRad = -normalizedAngle * Math.PI / 180;
    const tiltDim = moduleMesh.userData?.dir === 'port' ? moduleMesh.selSize.h : moduleMesh.selSize.w;

    // Keep update path consistent with placement path:
    // ballast tilt is applied around local X, while custom azimuth remains on Z.

    const roofAzimuth = shapeMesh?.oriAng?.azimuth ?? 0;
    const ballastedAzimuth = moduleMesh.userData.ballastedAzimuth ?? roofAzimuth;
    const azDiff = roofAzimuth - ballastedAzimuth;

    moduleMesh.rotation.set(0, 0, 0);
    moduleMesh.rotation.z = -azDiff * Math.PI / 180;
    moduleMesh.rotateX(tiltRad);
    moduleMesh.position.z = -Math.sin(Math.abs(tiltRad)) * (tiltDim / 2);

    moduleMesh.userData.ballastAngle = newBallastAngle;
    moduleMesh.userData.ballastedPitch = newBallastAngle;
    updateBallastAngleLabel(moduleMesh);
    UpdateMeshOBB(moduleMesh, 0);

}



const ModuleShared = (() => {
    const boxGeom = new THREE.BoxGeometry(1, 1, 0.02);

    const edgesGeom = new THREE.EdgesGeometry(boxGeom);
    const lineGeom = new LineSegmentsGeometry();
    lineGeom.setPositions(edgesGeom.attributes.position.array);

    // One line material for all outlines (world‑units linewidth).
    const lineMat = new LineMaterial({
        color: 0x000000,
        linewidth: 2,
        dashed: false,
        alphaToCoverage: true
    });
    lineMat.resolution.set(window.innerWidth, window.innerHeight);
    window.addEventListener("resize", () => {
        lineMat.resolution.set(window.innerWidth, window.innerHeight);
    });

    return { boxGeom, lineGeom, lineMat };
})();


// const ModuleMaterialTemplate = new THREE.MeshStandardMaterial({
//     color: 0xffffff,
// });

export function CreateModuleMesh(item, roofAzimuth = null) {
    const { x, z, selSize, dir, moduleId } = item;

    const moduleMesh = new InstancedModuleObject()


    SetModuleSize(moduleMesh, selSize, dir);

    // Core flags & metadata
    moduleMesh.meshType = "module";
    moduleMesh.name = "module";
    moduleMesh.position.set(x, z, -0.02);
    moduleMesh.module = true;
    moduleMesh.moduleId = moduleId;
    moduleMesh.userData.isBallastedRackType = item.isBallastedRackType;
    moduleMesh.verInfo = GetVerArr({ x, z }, selSize, dir, false, roofAzimuth);
    moduleMesh.wPosArr = [];

    // Reuse the shared line geometry & material; it scales with the parent mesh.
    const outline = new LineSegments2(ModuleShared.lineGeom, ModuleShared.lineMat);
    outline.name = "moduleOutline";
    moduleMesh.add(outline);

    SetMeshOBB(moduleMesh);
    return moduleMesh;
}


export function SetModuleSize(module, selSize, dir, virMesh, isBallasted) {
    
    const roofAzimuth = module.parent?.oriAng?.azimuth ?? 0;
    const thickness = isBallasted ? 0.5 : 1;
    
    const {dX, dZ} = GetModuleXZ(selSize, dir, true, roofAzimuth);
    
    if (virMesh) {
        module.scale.set(dX, thickness, dZ);
    } else {
        module.scale.set(dX, dZ, thickness);
        module.material.map = GetModuleMap(dir);
    }
    module.selSize = selSize;
    module.dir = dir;
    // module.verInfo = GetVerArr({ x: module.position.x, z: module.position.z }, selSize, dir, false, roofAzimuth);
}

export function SetSelModuleArr(moduleArr, module, event) {
    const select = event.button === 2 ? true : !module.select;
    const hideModuleTexture = useViewerStore.getState().moduleSolarAccess
    var irrMapFlag = false;
    if (hideModuleTexture) irrMapFlag = true;
    if (select) {
        const oldModule = moduleArr.find(item => item.select);
        const oldRoofId = oldModule ? oldModule.roofId : null;
        if (oldRoofId !== module.roofId) {
            ResetSelModuleArr(moduleArr)
        }
    } else {
        const showCol = irrMapFlag ? module.irrColor : norModuleCol;
        module.material.color.setHex(showCol);
    }
    const showCol = irrMapFlag ? module.irrColor : norModuleCol;
    module.material.color.setHex(select ? selModuleCol : showCol);
    module.material.needsUpdate = true;
    module.select = select;
    const selModuleArr = moduleArr.filter(item => {
        return item.select === true
    });
    module.first = selModuleArr.length === 1;
    SetMoveBtn(selModuleArr[0]);
}

export function ResetSelModuleArr(moduleArr) {
    const hideModuleTexture = useViewerStore.getState().moduleSolarAccess
    var irrMapFlag = false;
    if (hideModuleTexture) irrMapFlag = true;
    moduleArr.forEach(module => {
        const showCol = irrMapFlag ? module.irrColor : norModuleCol;
        module.material.color.setHex(showCol);
        module.material.emissive.set(0x000000);
        module.select = false;
        module.first = false;
    });
}

function GetSnapPos(pos, moduleArr, oriVerInfo, selModule) {
    const {moduleId, verInfo} = selModule, halfDX = verInfo.dX / 2, halfDZ = verInfo.dZ / 2;
    const nearModules = GetNearModules(moduleArr, oriVerInfo, moduleId, snapDis * 5, 'snapPos');
    var minDisX = Infinity, minDisZ = Infinity, snapPos = {...pos};

    let spaceSingle = space, snapDelta = snapDis * 1;
    const isBallastedRackTypeModuleActive = useViewerStore.getState().isBallastedRackTypeModuleActive;
    const activeBallastRackDirection = useViewerStore.getState().activeBallastRackDirection;

    let verticalRowSpace = spaceSingle
    let horizontalRowSpace = spaceSingle

    if(isBallastedRackTypeModuleActive){
        const isParallelRack = activeBallastRackDirection === 'parallel' || activeBallastRackDirection === 'horizontal';
        if (isParallelRack) {
            verticalRowSpace = GetRoundNum(18.875 * inch2m, 4);
            horizontalRowSpace = spaceSingle;
        } else if (activeBallastRackDirection === 'vertical') {
            verticalRowSpace = spaceSingle;
            horizontalRowSpace = GetRoundNum(18.875 * inch2m, 4);
        }
    }



    if(isBallastedRackTypeModuleActive){
        //reduce snapDelta
        snapDelta = snapDis * 3;
    }

    const unitDX = horizontalRowSpace + halfDX;
    const unitDZ = verticalRowSpace + halfDZ;

    [...nearModules].forEach(near => {
        const {x0, x1, z0, z1} = near.verInfo;
        if (pos.x > x0 && pos.x < x1) {
            const newX0 = x0 + halfDX, disX0 = Math.abs(pos.x - newX0);
            if (disX0 < snapDelta && disX0 < minDisX) {
                minDisX = disX0;
                snapPos.x = newX0;
            }
            const newX1 = x1 - halfDX, disX1 = Math.abs(pos.x - newX1);
            if (disX1 < snapDelta && disX1 < minDisX) {
                minDisX = disX1;
                snapPos.x = newX1;
            }
        } else if (x1 < pos.x) {
            const newX = x1 + unitDX, disX = Math.abs(pos.x - newX);
            if (disX < snapDelta && disX < minDisX) {
                minDisX = disX;
                snapPos.x = newX;
            }
        } else if (x0 > pos.x) {
            const newX = x0 - unitDX, disX = Math.abs(pos.x - newX);
            if (disX < snapDelta && disX < minDisX) {
                minDisX = disX;
                snapPos.x = newX;
            }
        }

        if (pos.z > z0 && pos.z < z1) {
            const newZ0 = z0 + halfDZ, disZ0 = Math.abs(pos.z - newZ0);
            if (disZ0 < snapDelta && disZ0 < minDisZ) {
                minDisZ = disZ0;
                snapPos.z = newZ0;
            }
            const newZ1 = z1 - halfDZ, disZ1 = Math.abs(pos.z - newZ1);
            if (disZ1 < snapDelta && disZ1 < minDisZ) {
                minDisZ = disZ1;
                snapPos.z = newZ1;
            }
        } else if (z1 < pos.z) {
            const newZ = z1 + unitDZ, disZ = Math.abs(pos.z - newZ);
            if (disZ < snapDelta && disZ < minDisZ) {
                minDisZ = disZ;
                snapPos.z = newZ;
            }
        } else if (z0 > pos.z) {
            const newZ = z0 - unitDZ, disZ = Math.abs(pos.z - newZ);
            if (disZ < snapDelta && disZ < minDisZ) {
                minDisZ = disZ;
                snapPos.z = newZ;
            }
        }
    });

    return snapPos;
}

export class MoveModuleState {
    constructor(moveOtherModules, moveOnRoof, moveModules, firstMoveModule, moveModuleArea, roofObst) {
        this.moveOtherModules = moveOtherModules;
        this.moveOnRoof = moveOnRoof;
        this.moveModules = moveModules;
        this.firstMoveModule = firstMoveModule;
        this.moveModuleArea = moveModuleArea;
        this.roofObst = roofObst;
    }
}

export function ReadyTransModules(self) {
    const {moduleMeshArr, roofMeshArr, virFlatPlane} = self;
    const moveModules = moduleMeshArr.filter(item => {
        return item.select === true
    });
    const firstMoveModule = moduleMeshArr.find(item => {
        return item.first === true
    }) || moveModules[0];
    const moveOnRoof = roofMeshArr.find(item => {
        return item.roofFaceId === firstMoveModule.roofId
    });
    moveModules.forEach((module) => snapshotModuleMoveStart(module, moveOnRoof));
    const {pos2DArr, posArr} = moveOnRoof;
    if (!moveOnRoof.posLimit) moveOnRoof.posLimit = GetLimitPosValue(pos2DArr);

    const {oriPos, selSize, dir, moduleId} = firstMoveModule, {dX, dZ} = GetModuleXZ(selSize, dir);
    var deltaMinX = dX / -2, deltaMaxX = dX / 2, deltaMinZ = dZ / -2, deltaMaxZ = dZ / 2;
    moveModules.filter(other => {
        return other.moduleId !== moduleId
    }).forEach(other => {
        const otherDXZ = GetModuleXZ(other.selSize, other.dir),
            deltaCX = other.oriPos.x - oriPos.x,
            deltaCZ = other.oriPos.y - oriPos.y;
        if (deltaCX - otherDXZ.dX / 2 < deltaMinX) {
            deltaMinX = deltaCX - otherDXZ.dX / 2;
        }
        if (deltaCX + otherDXZ.dX / 2 > deltaMaxX) {
            deltaMaxX = deltaCX + otherDXZ.dX / 2;
        }
        if (deltaCZ - otherDXZ.dZ / 2 < deltaMinZ) {
            deltaMinZ = deltaCZ - otherDXZ.dZ / 2;
        }
        if (deltaCZ + otherDXZ.dZ / 2 > deltaMaxZ) {
            deltaMaxZ = deltaCZ + otherDXZ.dZ / 2;
        }
    });

    const posCount = posArr.length, posLast = posArr[posCount - 1], posBefore = posArr[posCount - 2],
        posFirst = posArr[0];
    virFlatPlane.setFromCoplanarPoints(
        new THREE.Vector3(posLast.x, posLast.y, posLast.z),
        new THREE.Vector3(posFirst.x, posFirst.y, posFirst.z),
        new THREE.Vector3(posBefore.x, posBefore.y, posBefore.z)
    );

    const roofObst = [];

    for (const obst of self.obstGroup.children) {
        if (obst.roofId !== moveOnRoof.roofFaceId) {
            continue;
        }

        const p1 = new THREE.Vector3(obst.vPos.min.x, obst.vPos.min.y, obst.vPos.min.z);
        const p2 = new THREE.Vector3(obst.vPos.max.x, obst.vPos.max.y, obst.vPos.max.z);
        moveOnRoof.worldToLocal(p1);
        moveOnRoof.worldToLocal(p2);

        roofObst.push({min: p1, max: p2, obstId: obst.obstId});
    }

    return new MoveModuleState(
        moveOnRoof.children.filter(item => item.moduleId !== undefined && !item.select),
        moveOnRoof,
        moveModules,
        firstMoveModule,
        {deltaMinX, deltaMaxX, deltaMinZ, deltaMaxZ},
        roofObst,
    );
}

let mouse = new THREE.Vector2();
let raycaster = new THREE.Raycaster();

export function TransModule(e, self, state) {
    const {roofGroup, camera, cSize, posModuleDown, virFlatPlane, obstGroup, wallMeshArr} = self;

    const {posX, posY} = GetMousePos(e);
    mouse.x = (posX / cSize.w) * 2 - 1;
    mouse.y = -(posY / cSize.h) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
    const intersects = new THREE.Vector3();
    raycaster.ray.intersectPlane(virFlatPlane, intersects);

    const {pos2DArr, holeArr, posLimit, roofFaceId} = state.moveOnRoof, {minX, maxX, minZ, maxZ} = posLimit;
    const posDrag = state.moveOnRoof.worldToLocal(intersects);
    const mX = posDrag.x - posModuleDown.x, mY = posDrag.y - posModuleDown.y;

    const {oriPos} = state.firstMoveModule;
    const newPosX = oriPos.x + mX, newPosY = oriPos.y + mY;
    const {deltaMinX, deltaMaxX, deltaMinZ, deltaMaxZ} = state.moveModuleArea;
    const obstMeshArr = obstGroup.children.map(c => c.children[0].children[0].children[0]);

    const constrain = (pos) => {
        // constrains to movable roof area (externally adjusted for multiple selected modules)
        if (pos.x + deltaMinX - gap <= minX) pos.x = minX - deltaMinX + gap;
        else if (pos.x + deltaMaxX + gap >= maxX) pos.x = maxX - deltaMaxX - gap;
        if (pos.z + deltaMinZ - gap <= minZ) pos.z = minZ - deltaMinZ + gap;
        else if (pos.z + deltaMaxZ + gap >= maxZ) pos.z = maxZ - deltaMaxZ - gap;
    };

    let snapPos = {x: newPosX, z: newPosY};
    let minSnapDist = Infinity;
    let moduleSnap = undefined;

    // find the closest snap point among the selected modules
    for (const module of state.moveModules) {
        const p = {x: module.oriPos.x + mX, z: module.oriPos.y + mY};
        const pos = GetSnapPos(p, state.moveOtherModules, module.verInfo, module);
        const dx = pos.x - p.x;
        const dz = pos.z - p.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist > 0 && dist < minSnapDist) {
            snapPos = pos;
            minSnapDist = dist;
            moduleSnap = module;
        }
    }

    if (moduleSnap) {
        // align snap point relative to first selected module
        snapPos.x += oriPos.x - moduleSnap.oriPos.x;
        snapPos.z += oriPos.z - moduleSnap.oriPos.z;
    }

    constrain(snapPos);

    const {pathwayArr,setbackArr} = GetSetPathArr(roofGroup, roofFaceId);
    const deltaX = snapPos.x - oriPos.x, deltaZ = snapPos.z - oriPos.y;

    let isColliding = false;

    for (const item of state.moveModules) {
        const {oriPos} = item;

        item.position.x = oriPos.x + deltaX;
        item.position.y = oriPos.y + deltaZ;
        item.position.z = oriPos.z;

        if (!isColliding) {
            if (item.userData?.isBallastedRackType) {
                const status = getRackingModulePlacementStatus(
                    item,
                    state.moveOnRoof,
                    self,
                    state.moveOtherModules,
                    pathwayArr,
                    setbackArr,
                    obstMeshArr,
                );
                if (status !== ModulePlacementStatusOk) {
                    isColliding = true;
                }
            } else {
                item.verInfo = GetVerArr({x: item.position.x, z: item.position.y}, item.selSize, item.dir, false, state.moveOnRoof.oriAng.azimuth);
                const canModuleBePlaced = CanModuleBePlaced(item.verInfo, pos2DArr, holeArr, obstMeshArr, state.moveOnRoof, setbackArr, true, item, state.moveOtherModules, pathwayArr, wallMeshArr, true);
                if (canModuleBePlaced !== ModulePlacementStatusOk) {
                    self.modulePlacementStatus = canModuleBePlaced;
                    isColliding = true;
                }
            }
        }
    }

    if (!isColliding) {
        self.modulePlacementStatus = ModulePlacementStatusOk;
    }

    const isValidPlacement = !isColliding;
    const moduleColor = isValidPlacement ? selModuleCol : errModuleCol;

    const deltaPoint = {x: newPosX, z: newPosY};
    constrain(deltaPoint);

    for (let i = 0; i < state.moveModules.length; i++) {
        const item = state.moveModules[i];

        // Racking modules preview at drag position during move; revert to oriPos on mouseup via SetModuleOriPos
        if (isColliding && !item.userData?.isBallastedRackType) {
            item.position.x = item.oriPos.x + deltaPoint.x - oriPos.x;
            item.position.y = item.oriPos.y + deltaPoint.z - oriPos.y;
            item.position.z = item.oriPos.z;
        }

        item.flag = isValidPlacement;
        item.material.color.setHex(moduleColor);
        if (item.userData?.isBallastedRackType) {
            item.verInfo = getBallastedModuleVertices(item, state.moveOnRoof);
        } else {
            item.verInfo = GetVerArr({x: item.position.x, z: item.position.y}, item.selSize, item.dir, false, state.moveOnRoof.oriAng.azimuth);
        }
    }
}

function GetSelModuleInfo(self) {
    const {moduleMeshArr, roofMeshArr} = self,
        selModules = moduleMeshArr.filter(module => module.select === true),
        selRoofMesh = roofMeshArr.find(roofMesh => roofMesh.roofFaceId === (selModules[0] && selModules[0].roofId)) || {children: []},
        roofModules = selRoofMesh.children.filter(item => item.moduleId !== undefined),
        deselModeArr = roofModules.filter(module => module.select !== true)
    return {selModules, selRoofMesh, deselModeArr}
}

export function UpdateSelModuleArr(self, type, info) {
    const {selModules, selRoofMesh, deselModeArr} = GetSelModuleInfo(self);
    const {inch, dirStr} = info;
    const {pos2DArr, holeArr, roofFaceId} = selRoofMesh;
    const {pathwayArr,setbackArr} = GetSetPathArr(self.roofGroup, roofFaceId);
    const obstMeshArr = self.obstGroup.children.map(c => c.children[0].children[0].children[0]);
    const oldModuleArrInfo = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout));
    const oldVerInfoArr = [], oldPosArr = [], oldDirArr = [],oldUserData = [];
    var flagModule = true;
    const currentModuleArrInfo = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout));
    const currentRoofModules = currentModuleArrInfo.find(r => r.roofFaceId === roofFaceId);
    selModules.forEach((module, mIdx) => {
        const {position, selSize, dir, moduleId} = module;
        oldPosArr[mIdx] = {...module.position};
        oldVerInfoArr[mIdx] = {...module.verInfo};
        oldDirArr[mIdx] = module.dir;
        // Copy userData so revert restores original ballast state (module.userData is mutated when spinning)
        oldUserData[mIdx] = {...module.userData};
        const curIdx = currentRoofModules.moduleArr.findIndex(module => module.moduleId === moduleId);
        if (type === 'moveInch') {
            const dirRoof = selRoofMesh.rotX >= 0 ? 1 : -1,
                axis = (dirStr === 'top' || dirStr === 'bottom') ? 'y' : 'x',
                dirMove = (dirStr === 'left' || dirStr === 'top') ? -1 : 1;
            module.position[axis] += inch * inch2m * dirMove * dirRoof;
            if (curIdx !== -1) {
                currentRoofModules.moduleArr[curIdx][axis] += inch * inch2m * dirMove * dirRoof;
            }
        } else if (type === 'dir') {
            module.dir = info;
            SetModuleSize(module, module.selSize, info);
            if (curIdx !== -1) {
                currentRoofModules.moduleArr[curIdx].dir = info;
            }

            //if it was ballast
            if(module.userData.isBallastedRackType){
                //Switch the ballast rack direction
                module.userData.ballastRackDirection = module.userData.ballastRackDirection === 'horizontal' ? 'vertical' : 'horizontal';

                //now update the rotation and position based on new direction
                const associatedRoof = self.roofMeshArr.find(r => r.roofFaceId === module.roofId);

                const associatedRoofPitch = Number(associatedRoof?.oriAng?.tilt || 0)
                const ballastAzimuth = self.ballastAzimuth;
                const ballastAngle = self.ballastAngle;

                module.rotation.set(0,0,0);
                module.rotateY(degToRad(ballastAngle));
                module.userData.ballastRackDirection = 'vertical';
                module.userData.ballastAzimuth = ballastAzimuth;
                module.userData.ballastAngle = ballastAngle;


            }

        }
        const newVerInfo = GetVerArr({x: position.x, z: position.y}, selSize, module.dir, false, selRoofMesh.oriAng.azimuth);
        module.verInfo = {...newVerInfo};
        const canModuleBePlaced = CanModuleBePlaced(newVerInfo, pos2DArr, holeArr, obstMeshArr, selRoofMesh, setbackArr, true, module, deselModeArr, pathwayArr, self.wallMeshArr,true);
        if (canModuleBePlaced !== ModulePlacementStatusOk) {
            flagModule = false;
        }
    });

    if (flagModule) { // Can place the module
        siteCalculationState.needsUpdate(MODULE_GRAPH);
        siteCalculationState.useCalculations(MODULE_GRAPH);
        useViewerStore.setState({savedRoofModulesLayout: currentModuleArrInfo});
        self.addStep(EditAction.ModuleLayout, 2, JSON.stringify(oldModuleArrInfo), JSON.stringify(currentModuleArrInfo));
        return;
    }
    // If module cannot be placed update color to red
    setTimeout(() => {
        selModules.forEach(module => {
            module.material.color.setHex(errModuleCol);
        });
    }, 0);
    // Reset modules to original state if cannot be placed after a delay
    setTimeout(() => {
        selModules.forEach((module, mIdx) => {
            module.material.color.setHex(selModuleCol);
            module.verInfo = oldVerInfoArr[mIdx];
            module.dir = oldDirArr[mIdx];
            module.position.x = oldPosArr[mIdx].x;
            module.position.y = oldPosArr[mIdx].y;
            module.position.z = oldPosArr[mIdx].z;

            if(module.userData.isBallastedRackType && oldUserData[mIdx]){
                // Restore original ballast direction and angle (oldUserData is a copy made before spin)
                module.userData.ballastRackDirection = oldUserData[mIdx].ballastRackDirection;
                module.userData.ballastAngle = oldUserData[mIdx].ballastAngle;

                // Re-apply rotation from original ballast direction and angle
                const associatedRoof = self.roofMeshArr.find(r => r.roofFaceId === module.roofId);
                const associatedRoofPitch = Number(associatedRoof?.oriAng?.tilt || 0);
                const normalizedBallastAngle = module.userData.ballastAngle - associatedRoofPitch;

                module.rotation.set(0, 0, 0);
                if(module.userData.ballastRackDirection === 'horizontal') {
                    module.rotateX(-normalizedBallastAngle * Math.PI / 180);
                } else {
                    module.rotateY(-normalizedBallastAngle * Math.PI / 180);
                }
            }

            SetModuleSize(module, module.selSize, module.dir);
            SetModuleAsColored(module,module.irrColor)

        });
    }, timeModuleDelay);
}


function SetBtnDirClass(dir) {
    const btnPort = document.getElementsByClassName('btnModulePort')[0];
    const btnLand = document.getElementsByClassName('btnModuleLand')[0];
    if (!btnPort || !btnLand) return;
    if (dir === 'port') {
        btnPort.classList.add('active');
        btnLand.classList.remove('active');
    } else if (dir === 'land') {
        btnPort.classList.remove('active');
        btnLand.classList.add('active');
    }
}

function SetMoveBtn(select) {
    const btnModuleMove = document.getElementsByClassName('btnModuleMove')[0];
    const btnModuleDel = document.getElementsByClassName('btnModuleDel')[0];
    if (!btnModuleMove || !btnModuleDel) return;
    if (select) {
        btnModuleMove.classList.remove('disabled');
        btnModuleDel.classList.remove('disabled');
    } else {
        btnModuleMove.classList.add('disabled');
        btnModuleDel.classList.add('disabled');
    }
}


export function GetPosModuleDown(interInfo, moduleMeshArr, roofMeshArr) {
    const selModules = GetSelModules(moduleMeshArr);
    if (!selModules || !selModules.length) return;
    const roofMesh = interInfo?.object
        ?? roofMeshArr?.find((roof) => roof.roofFaceId === selModules[0].roofId);
    selModules.forEach((item) => snapshotModuleMoveStart(item, roofMesh));
    if (!interInfo || !roofMesh) return;
    if (selModules[0].roofId !== roofMesh.roofFaceId) return;
    const posLocal = roofMesh.worldToLocal(interInfo.point);
    return posLocal;
}

export function GetSelModules(meshArr) {
    return meshArr.filter(item => item.select === true);
}

export function InitModulePush(self) {
    let {moduleMeshArr, virModule, moduleDir} = self;
    ResetSelModuleArr(moduleMeshArr);
    const isBallastedRackTypeModuleActive = useViewerStore.getState().isBallastedRackTypeModuleActive;
    if(isBallastedRackTypeModuleActive){
        moduleDir = 'land';
    }
    SetModuleSize(virModule.children[0], useViewerStore.getState().activeModule, moduleDir, true);
}

function highlightRowBeingUpdatedInTable(selRoofMesh) {
    //get data-roofId
        const roofRowInTable = document.querySelector(`[data-roofid="${selRoofMesh.roofFaceId}"]`);

    //blink the row in the table
    if (roofRowInTable) {
        roofRowInTable.classList.add('highlightRow');
        setTimeout(() => {
            roofRowInTable.classList.remove('highlightRow');
        }, 1000);
    }
}

export function SetVirModulePos(self, e) {
    const {virModule, roofMeshArr, camera, cSize, obstGroup, roofGroup} = self;
    const interInfo = GetClickObj(e, roofMeshArr, camera, cSize);
    virModule.visible = interInfo ? true : false;
    if (!interInfo) return;
    const virMesh = virModule.children[0];
    const {selSize, dir} = virMesh;
    const {point, object} = interInfo,
        {rotX, rotY, posArr, roofFaceId, pos2DArr, holeArr, oriAng} = object,
        {azimuth, tilt} = oriAng,
        lastNode = posArr[posArr.length - 1];
    const newPos2DInfo = GetShapePosArr([point, ...posArr], tilt, azimuth, lastNode);
    const cPos = newPos2DInfo.pos2DArr[0];
    const roofModules = object.children.filter(item => item.moduleId !== undefined);
    const oriVerInfo = GetVerArr(cPos, selSize, dir, false, object.oriAng.azimuth);
    if (!object.posLimit) object.posLimit = GetLimitPosValue(pos2DArr);

    const snapPos = GetSnapPos(cPos, roofModules, oriVerInfo, {moduleId: '', verInfo: oriVerInfo});
    const {minX, maxX, minZ, maxZ} = object.posLimit,
        halfDX = oriVerInfo.dX / 2, halfDZ = oriVerInfo.dZ / 2;
    if (snapPos.x - halfDX < minX + inch4) snapPos.x = minX + inch4 + halfDX;
    if (snapPos.x + halfDX > maxX - inch4) snapPos.x = maxX - inch4 - halfDX;
    if (snapPos.z - halfDZ < minZ + inch4) snapPos.z = minZ + inch4 + halfDZ;
    if (snapPos.z + halfDZ > maxZ - inch4) snapPos.z = maxZ - inch4 - halfDZ;
    const snapVerInfo = GetVerArr(snapPos, selSize, dir, false, object.oriAng.azimuth);
    const cloneTest = testMesh.clone();
    object.add(cloneTest);
    cloneTest.position.set(snapPos.x, snapPos.z, 0);
    const worldPos = cloneTest.getWorldPosition(new THREE.Vector3());
    object.remove(cloneTest);

    const {pathwayArr ,setbackArr} = GetSetPathArr(roofGroup, roofFaceId);
    const obstMeshArr = obstGroup.children.map(c => c.children[0].children[0].children[0]);

    virMesh.verInfo = snapVerInfo;
    const canModuleBePlaced = CanModuleBePlaced(snapVerInfo, pos2DArr, holeArr, obstMeshArr, object, setbackArr, true, virMesh, roofModules, pathwayArr, self.wallMeshArr, true);
    virModule.flag = canModuleBePlaced === ModulePlacementStatusOk;
    self.modulePlacementStatus = canModuleBePlaced;

    // useViewerStore.setState(() => ({modulePlacementStatus: canModuleBePlaced}));

    
    // virModule.rotation.y = rotY
    // virModule.originalRotationY = rotY;
    	
    let yPos = worldPos.y;
    let finalRotY = rotY;
    let finalRotX = rotX;
    const isBallastedRackTypeModuleActive = useViewerStore.getState().isBallastedRackTypeModuleActive;
    
    if (isBallastedRackTypeModuleActive) {
        const ballastAzimuth = self.ballastAzimuth;
        finalRotX = GetRadVal(self.ballastAngle);

        const azDiff = azimuth - ballastAzimuth;
        finalRotY = rotY + GetRadVal(azDiff) + Math.PI;
        yPos = worldPos.y + Math.sin(Math.abs(finalRotX)) * (selSize.w / 2);
        
    }

    virModule.position.set(worldPos.x, yPos, worldPos.z);
    virModule.rotation.y = finalRotY;
    virModule.originalRotationY = finalRotY;

    virModule.children[0].rotation.x = finalRotX;

    virModule.roofId = roofFaceId;
    virModule.snapPos = snapPos;
    virModule.children[0].material.color.setHex(virModule.flag ? addModuleCol : errModuleCol);
}

function CanModuleBePlaced(verInfo, pos2DArr, holeArr, obstArr, roofMesh, setbackArr, flagPath, module, moduleArr, pathwayArr, wallMeshArr, isUserAction = false, obstructionInfoOut = null) {
    const sceneViewer = useViewerStore.getState().sceneViewer;
    if (roofMesh && useViewerStore.getState().accessoryBuildingList.includes(roofMesh.userData.buildingName)) {
        sceneViewer.modulePlacementStatus = ModulePlacementStatusRoofShape;
        return ModulePlacementStatusRoofShape;
    }

    if(roofMesh && roofMesh.disableModule){
        sceneViewer.modulePlacementStatus = ModulePlacementStatusDeactivatedRoof;
        return ModulePlacementStatusDeactivatedRoof;
    }

    let isBallastedRackTypeModuleActive = useViewerStore.getState().isBallastedRackTypeModuleActive;

    if(module.userData.isBallastedRackType){
        isBallastedRackTypeModuleActive = true;
    }

    if(!isBallastedRackTypeModuleActive){
        if (isRoofTooSteep(roofMesh.oriAng.tilt)) {
            sceneViewer.modulePlacementStatus = ModulePlacementStatusRoofPitch;
            return ModulePlacementStatusRoofPitch;
        }
    }


    const modulePos = CheckModulePos(
        verInfo,
        pos2DArr,
        holeArr,
        obstArr,
        setbackArr,
        flagPath,
        module,
        wallMeshArr,
        roofMesh,
        isUserAction,
        obstructionInfoOut
    );
    if (modulePos !== ModulePlacementStatusOk) {

        return modulePos;
    }

    if (module && !CheckModuleOver(module, moduleArr)) {
        sceneViewer.modulePlacementStatus = ModulePlacementStatusModule;
        return ModulePlacementStatusModule;
    }

    const overPathIds = GetOverPathIds(verInfo, pathwayArr);
    if (overPathIds.length === 1) {
        const selPath = pathwayArr.find(pathway => overPathIds.includes(pathway.pathwayId));
        if (selPath.pathActive) {
            sceneViewer.modulePlacementStatus = ModulePlacementStatusPathway;
            return ModulePlacementStatusPathway;
        }


    } else {
        //check even if one of the pathways is active and the module is not allowed to be placed on active pathways and break the loop
        for (let i = 0; i < overPathIds.length; i++) {
            const selPath = pathwayArr.find(pathway => overPathIds[i] === pathway.pathwayId);
            if (selPath.pathActive) {
                sceneViewer.modulePlacementStatus = ModulePlacementStatusPathway;
                return ModulePlacementStatusPathway;
            }
        }

    }

    module.overPathIds = overPathIds;
    sceneViewer.modulePlacementStatus = ModulePlacementStatusOk;
    return ModulePlacementStatusOk;
}

export function SetVirModuleSize(virModule, type, value) {
    const virModuleMesh = virModule.children[0];
    if (type === 'dir') SetModuleSize(virModuleMesh, virModuleMesh.selSize, value, true);
    else if (type === 'size') {
        SetModuleSize(virModuleMesh, value, virModuleMesh.dir, true);
    }
}

export async function CreateModule(self) {
    const {virModule, roofMeshArr, moduleMeshArr, meshArr, roofGroup} = self;
    if (!virModule.visible || !virModule.flag) return;
    const {roofId, snapPos} = virModule,
        selRoof = roofMeshArr.find(item => {
            return item.roofFaceId === roofId
        });
    if (selRoof.disableModule) return;
    await ensureRoofShadeReadyForModulePlacement(self, roofId);


    const {selSize, dir} = virModule.children[0];
    const activeBallastRackDirection = useViewerStore.getState().activeBallastRackDirection;
    const isBallastedActive = useViewerStore.getState().isBallastedRackTypeModuleActive;
    const customAzimuth = useViewerStore.getState().customAzimuth;

    const currentModArr = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout));
    // const item = {x: GetRoundNum(snapPos.x, 4), z: GetRoundNum(snapPos.z, 4), selSize, dir, moduleId: GetIdStr()};
    const item = {
        x: GetRoundNum(snapPos.x, 4),
        z: GetRoundNum(snapPos.z, 4),
        selSize,
        dir,
        moduleId: GetIdStr(),
        isBallastedRackType: isBallastedActive,
        ballastRackDirection: activeBallastRackDirection,
        azimuth: isBallastedActive ? self.ballastAzimuth : undefined,
        ballastAngle: isBallastedActive ? self.ballastAngle : undefined,
    };

    // deep copy
    const modeArrInfo = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout));
    const oldRoof = modeArrInfo.find(r => r.roofFaceId === roofId);


    oldRoof?.moduleArr.push(item);

    //if moduleArr is empty, then add the roof to the modeArrInfo
    if (!selRoof.moduleArr) {
        selRoof.moduleArr = [];
    }
    selRoof.moduleArr.push(item);

    useViewerStore.setState({savedRoofModulesLayout: modeArrInfo});
    const verInfo = GetVerArr({x: item.x, z: item.z}, selSize, dir, false, selRoof.oriAng.azimuth);

    self.addStep(EditAction.ModuleLayout, item.moduleId, JSON.stringify(currentModArr), JSON.stringify(modeArrInfo)); // Updated for undo-redo changes

    const {pathwayArr} = GetSetPathArr(roofGroup, roofId);
    item.overPathIds = GetOverPathIds(verInfo, pathwayArr);

    AddIndividualMode(selRoof, item, true, moduleMeshArr, meshArr, useViewerStore.getState().isModuleMultiplierLabelVisible);
    highlightRowBeingUpdatedInTable(selRoof);
    UpdateSetbackDepth(self);
    siteCalculationState.needsUpdate(MODULE_GRAPH);
    siteCalculationState.useCalculations(MODULE_GRAPH);
}

export function DeleteModule(selModules, selRoof, moduleMeshArr, self, type) {
    const selectedDesignIndex = JSON.parse(JSON.stringify(useViewerStore.getState().selectedDesignIndex));
    const {roofFaceId} = selRoof, modeArrInfo = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout));
    const oldModeArrInfo = JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout));
    const oldRoof = modeArrInfo.find(r => r.roofFaceId === roofFaceId);

    selModules.forEach(module => {
        const selModuleId = module.moduleId;
        const idx = moduleMeshArr.findIndex(item => item.moduleId === selModuleId);
        moduleMeshArr.splice(idx, 1); // shift
        const selMesh = selRoof.children.find(item => item.moduleId === selModuleId);
        selRoof.remove(selMesh);
        self.meshArr = self.meshArr.filter(item => item.moduleId !== selModuleId);
        const oldIdx = oldRoof?.moduleArr.findIndex(oldModule => oldModule.moduleId === selModuleId);
        oldRoof?.moduleArr.splice(oldIdx, 1);
        selRoof?.moduleArr.splice(oldIdx, 1);
        const labels = module.children.filter(c =>
            c.meshType === 'moduleLabel' || c.name === 'ballastAngleLabel'
        );
        for (const label of labels) {
            module.remove(label);
        }
    });


    if (!type) {
        self.addStep(EditAction.ModuleLayout, 5, JSON.stringify(oldModeArrInfo), JSON.stringify(modeArrInfo));//updated for module delete redo
        useViewerStore.setState({savedRoofModulesLayout: modeArrInfo});
    }
    if(!isGroundMount(selRoof)){
        UpdateSetbackDepth(self);
    }
    highlightRowBeingUpdatedInTable(selRoof);
    const isContainingBallastedRacking = moduleMeshArr.some(module => module.userData.isBallastedRackType);
    if(moduleMeshArr.length > 0 && isContainingBallastedRacking) {
        check25PercentRuleForBallastedRacking(moduleMeshArr);
    }

    setTimeout(() => {
        siteCalculationState.needsUpdate(MODULE_GRAPH);
        siteCalculationState.useCalculations(MODULE_GRAPH);
    }, 100);

}

export function SwitchModuleIrrMat(self, value) {
    const {roofs, modelGroup, roofMeshArr} = self;
    if (!roofs || !roofs[0] || !roofs[0].faces || !roofs[0].faces.length) return;
    if (value) {
        const activeRoofMeshArr = roofMeshArr.filter(item => !item.disableModule);
        modelGroup.totalShadeCount = activeRoofMeshArr.length;
        modelGroup.irrType = 'module';
        siteCalculationState.useCalculations(SHADE_CALCULATIONS);
    } else {
        modelGroup.irrType = null;
        SetShadeDisplayEffect(modelGroup);
    }
}

export function SetShadeMeshOnModule(roofMeshArr) {
    roofMeshArr.forEach((roofMesh) => {
        const {size, children} = roofMesh;
        if (size && size < 52) return;
        const shadeArr = roofMesh.userData.shadeArrForRoof;
        const moduleMeshArr = getRoofModuleMeshes(roofMesh);
        shadeArr.forEach((shade) => {
            shade.onModule = false;
            if (CheckInShade(shade, moduleMeshArr)) {
                shade.onModule = true;
            }
        });
    });
}

function computeMonthlyProduction(annualProd, monthlyWeightArr) {
    const monthlyProd = [];
    if(monthlyWeightArr && annualProd){
        for (let m = 0; m < 12; m++) {
            monthlyProd[m] = annualProd * monthlyWeightArr[m]
        }
    }
    return monthlyProd;
}

var wasMonthlyWeightArrRebuild = false;

function populateMonthlyIdealMult(annualIdeal) {
    const idealMulti = annualIdeal / 12;
    return Object.keys(Month)
    .filter(month => isNaN(month))
    .map((month) => {
        return {
            month: month,
            multiplier: idealMulti,
        }
    });
}

export function computeTreeImpactMap(self) {
    const { roofMeshArr } = self;
    const angArr = useViewerStore.getState().savedAccessMultipliersForSolarRoofs;
    const facePtInfo = cloneDeep(useViewerStore.getState().savedRoofWiseAccessMultiplierDataArray);

    facePtInfo.forEach(face => {
        const ptVal = parseInt(face.ptHashAverage);
        const azimuth = GetRoundNum(parseFloat(face.azimuth), 4);
        const tilt = GetRoundNum(parseFloat(face.tilt), 2);
        const selProdAng = angArr.find(item => GetRoundNum(item.azimuth, 4) === azimuth && GetRoundNum(item.tilt, 2) === tilt && item.multipliers);
        face.multipliers = selProdAng?.multipliers ?? [];
    });

    let total_module_count = 0;
    let total_multiplier = 0;
    const treeGhostMultArr = [];
    const treeArr = [];
    self.treeGroup.children.forEach(t => treeArr.push(t.userData.treeId));

    roofMeshArr.forEach((roofMesh) => {
        const shadeArr = roofMesh.userData.shadeArrForRoof;
        const moduleMeshArr = getRoofModuleMeshes(roofMesh);
        const faceInfo = facePtInfo.find(f => f.roofFaceId === roofMesh.roofFaceId);

        if (roofMesh.userData.roof_size_type === 'small_face' || roofMesh.disableModule || !faceInfo) {
            return;
        }

        const { multipliers } = faceInfo;
        let module_count = 0;
        let solar_access = 0;
        let treeImpactRoofArr = [];

        moduleMeshArr.forEach(module => {
            module_count++;
            const { irrPro, treeImpactModuleArr } = GetIrrPro(module.verInfo, shadeArr, treeArr);
            solar_access += irrPro;
            treeArr.forEach(treeId => {
                const treeImpactModule = treeImpactModuleArr.find(t => t.treeId === treeId);
                const idx = treeImpactRoofArr.findIndex(data => data.treeId === treeId);
                if (idx === -1) {
                    treeImpactRoofArr.push({ treeId, solar_access: treeImpactModule?.irrPro || 0 });
                } else {
                    treeImpactRoofArr[idx].solar_access += treeImpactModule?.irrPro || 0;
                }
            });
        });

        solar_access = module_count ? Math.round(solar_access / module_count) : 0;
        const prodInfo = multipliers.find(item => item.access === Math.floor(solar_access));
        const roof_multiplier = prodInfo?.multiplier ?? 0;
        total_multiplier += roof_multiplier * module_count;
        total_module_count += module_count;

        treeImpactRoofArr.forEach(tree => {
            const treeSolarAvg = module_count ? Math.round(tree.solar_access / module_count) : 0;
            const treeProdInfo = multipliers.find(item => item.access === treeSolarAvg);
            const treeGhostRoofMulti = treeProdInfo?.multiplier ?? 0;
            const roofTotalMulti = treeGhostRoofMulti * module_count;
            const idx = treeGhostMultArr.findIndex(data => tree.treeId === data.treeId);
            if (idx === -1) {
                treeGhostMultArr.push({ treeId: tree.treeId, total_multiplier: roofTotalMulti });
            } else {
                treeGhostMultArr[idx].total_multiplier += roofTotalMulti;
            }
        });
    });

    const multiplier = total_module_count === 0 ? 0 : GetRoundNum(total_multiplier / total_module_count, 3);
    const treeImpactMap = {};
    treeGhostMultArr.forEach(tree => {
        const treeGhostMultiplier = total_module_count === 0 ? 0 : GetRoundNum(tree.total_multiplier / total_module_count, 3);
        treeImpactMap[tree.treeId] = treeGhostMultiplier - multiplier;
    });
    return treeImpactMap;
}

/** Recompute activeTreeImpact for ghosted trees as if each were not ghosted (runs after add/move/other tree changes). */
function refreshGhostedTreesActiveTreeImpact(self) {
    if (!isGhostOnlyGreenImpactTreesEnabled()) return;

    const ghostedTrees = self.treeGroup.children.filter(t => t.delStatus);
    if (!ghostedTrees.length) return;

    ghostedTrees.forEach(tree => {
        if (shouldSkipGhostImpactReevaluation(tree) || consumeGhostReevalDeferral(tree)) {
            return;
        }

        const treeId = tree.userData.treeId;
        syncRayDelStatusForTrees([treeId], false);
        RefreshTreeImpactArr(self);
        const impactMap = computeTreeImpactMap(self);
        if (!Object.prototype.hasOwnProperty.call(impactMap, treeId)) {
            syncRayDelStatusForTrees([treeId], true);
            return;
        }
        tree.userData.activeTreeImpact = impactMap[treeId];
        tree.userData.hasReevaluatedSinceGhost = true;
        syncRayDelStatusForTrees([treeId], true);
    });
    syncAllTreeRayDelStatusFromMeshes(self);
    RefreshTreeImpactArr(self);
}

export async function SetModuleGraph(self) {
    const _perfT0 = perfNow();
    const {roofMeshArr} = self;
    const roofInfoArr = [];
    syncAllTreeRayDelStatusFromMeshes(self);
    const tShadeOnMod = perfNow();
    SetShadeMeshOnModule(roofMeshArr);
    perfLog('SetModuleGraph SetShadeMeshOnModule', perfNow() - tShadeOnMod, `roofs=${roofMeshArr?.length ?? 0}`);
    let totalWeightedSolarAccess = 0;


    const angArr = useViewerStore.getState().savedAccessMultipliersForSolarRoofs;

    // deep copy
    const angIdeal = cloneDeep(useViewerStore.getState().savedRoofWiseIdealMultiplier);
    angIdeal.forEach(ang => {
        ang.tilt = parseFloat(ang.tilt);
        ang.azimuth = parseFloat(ang.azimuth);
        ang.ideal = ang.idealMonthly ? parseFloat(ang.ideal) : parseFloat(ang.ideal / 10000);
        ang.idealMonthly = ang.idealMonthly ?? populateMonthlyIdealMult(ang.ideal);
    });

    // deep copy
    const facePtInfo = cloneDeep(useViewerStore.getState().savedRoofWiseAccessMultiplierDataArray);

    let totalTsrf = 0, totalArea = 0, totalMultiplier = 0;

    const isMonthlyWeightArrExists = Array.isArray(self.roofs?.[0]?.faces?.[0]?.monthlyWeightArr) && self.roofs[0].faces[0].monthlyWeightArr.length === 12;
    if(!isMonthlyWeightArrExists && !wasMonthlyWeightArrRebuild){
        wasMonthlyWeightArrRebuild = true;
        const tRebuild = perfNow();
        await reBuildMonthlyWeightArrIfNotExist(self);
        perfLog('SetModuleGraph reBuildMonthlyWeightArrIfNotExist', perfNow() - tRebuild);
    }

    const tFacePt = perfNow();
    facePtInfo.forEach(face => {
        face.size = parseFloat(face.size);
        const ptVal = parseInt(face.ptHashAverage);
        const azimuth = GetRoundNum(parseFloat(face.azimuth), 4);
        const tilt = GetRoundNum(parseFloat(face.tilt), 2);
        const selProdAng = angArr.find(item=> GetRoundNum(item.azimuth, 4) === azimuth && GetRoundNum(item.tilt, 2) === tilt && item.multipliers);
        const multipliers = selProdAng?.multipliers ?? [];
        const prodInfo = multipliers.find(item => item.access === ptVal);
        face.multiplier = prodInfo ? prodInfo.multiplier : 0;
        face.multipliers = multipliers;

        const selIdealAng = angIdeal.find(item => GetRoundNum(item.tilt, 2) === tilt);
        face.idealMulti = selIdealAng ? selIdealAng.ideal : 0;
        const idealMulti = face.idealMulti;
        face.TSRF = idealMulti ? face.multiplier / idealMulti : 0;
        totalArea += face.size;
        totalTsrf += face.TSRF * face.size;
        totalMultiplier += face.multiplier * face.size;
    });
    perfLog('SetModuleGraph facePtInfo loop', perfNow() - tFacePt, `faces=${facePtInfo?.length ?? 0}`);

    let roofAreaSize = 0;
    let weighted_efficiency = 0;
    let total_module_count = 0;
    let totalSystemSize = 0;
    let actualWattage = 0;
    let utilityWattage = 0;
    let moduleMultiplierTotal = 0;
    let total_multiplier = 0;
    let treeGhostMultArr = [];

    const modulePower = useViewerStore.getState().activeModule.power;

    let treeArr = [];
    self.treeGroup.children.forEach(t => treeArr.push(t.userData.treeId));

    const tRoofLoop = perfNow();
    roofMeshArr.forEach((roofMesh, roofIdx) => {
        const roofT0 = perfNow();
        const {oriAng, size, children, roofFaceId} = roofMesh, {azimuth, tilt} = oriAng;
        const roofSize = size ?? 0;
        let array_size = 0;

        const shadeArr = roofMesh.userData.shadeArrForRoof
        const roofInShadeArr = shadeArr.filter(shade => shade.onModule === true);
        const moduleMeshArr = getRoofModuleMeshes(roofMesh);

        roofAreaSize += GetRoundNum(roofSize, 2);
        let totalShadeModule = 0;
        roofInShadeArr.forEach(shade => {
            const monthArr = shade.monthArr;
            const monthValAvg = monthArr?.reduce((p, c) => p + c, 0);
            totalShadeModule += monthValAvg;
        });

        let solarAccessValue = 0;
        if (totalShadeModule && roofInShadeArr.length) {
            solarAccessValue = (totalShadeModule / roofInShadeArr.length).toFixed(1);
        }

        let solar_access = 0;
        let efficiency = 0;
        let module_count = 0;
        let treeImpactRoofArr = [];

        const faceInfo = facePtInfo.find(f => f.roofFaceId === roofMesh.roofFaceId);
        if (roofMesh.userData.roof_size_type === 'small_face' || roofMesh.disableModule ||!faceInfo) {
            roofInfoArr.push({
                buildingName: roofMesh.userData.buildingName,
                roofFaceId,
                roofNum: roofMesh.userData.roof_tag_id,
                arraySize: (array_size / 1000).toFixed(3),
                azimuth: azimuth.toFixed(1),
                ballastPitch: roofMesh.userData.ballastedPitch,
                ballastAzimuth: roofMesh.userData.ballastedAzimuth,
                pitch: tilt.toFixed(1),
                solarAccess: solarAccessValue.toString(),
                efficiency: (efficiency * 100).toFixed(1),
                modules: moduleMeshArr.length.toString(),
                utility_production: '0',
                actual_production: '0',
                pitchOverride: roofMesh.userData.pitchOverride,
                nameOverride: roofMesh.userData.nameOverride,
            });
            if (isModulePerfLogEnabled() && perfNow() - roofT0 >= 120) {
                perfLog('SetModuleGraph roof (early exit)', perfNow() - roofT0, roofFaceId);
            }
            return;
        }

        const {multipliers, idealMulti} = faceInfo;
        const access_multipliers = angArr.find(item => item.azimuth === roofMesh.oriAng.azimuth && item.tilt === roofMesh.oriAng.tilt)
            ?.multipliers;
        const default_multiplier = access_multipliers?.find(item => item.access === 0)?.multiplier ?? 0;

        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
            'August', 'September', 'October', 'November', 'December'];
        const monthlyProductionEstimateData = {};
        monthNames.forEach(month => [
            monthlyProductionEstimateData[month] =
            {
                solar_access: 0,
                irradiance: 0,
                actual_production: 0,
                efficiency: 0,
            }
        ]);

        const monthlyIrr = CalculateIrradiancePerRoof(self);
        const thisIdeal = angIdeal.find(f => f.tilt === tilt)
        const monthlyIdealMults = thisIdeal?.idealMonthly ?? [];
        let monthlyWeightArr = self.roofs[0].faces.find(f => f.roofFaceId === roofFaceId).monthlyWeightArr
        if(!monthlyWeightArr || monthlyWeightArr.length===0) reBuildMonthlyWeightArrIfNotExist(self);

        moduleMeshArr.forEach(module => {
            module_count++;
            total_module_count++;

            for (let i = 0; i < 12; i++) {
                monthlyProductionEstimateData[monthNames[i]].solar_access += getIrrProForGivenMonthIdx(module.verInfo, shadeArr, i + 1) / moduleMeshArr.length;
                const prodInfo = multipliers.find(item => item.access === Math.floor(monthlyProductionEstimateData[monthNames[i]].solar_access));
                const module_multiplier = prodInfo?.monthlyMultipliers?.find(m => m.month === monthNames[i])?.multiplier ?? 0;
                const module_efficiency = monthlyIdealMults?.[i]?.multiplier ? module_multiplier / monthlyIdealMults[i].multiplier : 0;
                monthlyProductionEstimateData[monthNames[i]].efficiency += module_efficiency;
                monthlyProductionEstimateData[monthNames[i]].irradiance = monthlyIrr[roofMesh.roofFaceId][i];
            }

            array_size += modulePower;

            const {irrPro, treeImpactModuleArr} = GetIrrPro(module.verInfo, shadeArr, treeArr);
            module.irrPro = irrPro;
            solar_access += irrPro;
            totalWeightedSolarAccess += irrPro;
            const prodInfo = multipliers.find(item => item.access === Math.floor(irrPro));
            const module_multiplier = prodInfo?.multiplier ?? 0;
            module.multiplier = module_multiplier;
            moduleMultiplierTotal += module_multiplier;

            treeArr.forEach(treeId => {
                const treeImpactModule = treeImpactModuleArr.find(t => t.treeId === treeId);
                const idx = treeImpactRoofArr.findIndex(data => data.treeId === treeId);
                if (idx === -1) {
                    treeImpactRoofArr.push({treeId, solar_access: treeImpactModule?.irrPro || 0});
                } else {
                    treeImpactRoofArr[idx].solar_access += treeImpactModule?.irrPro || 0;
                }
            });

            const module_efficiency = idealMulti ? module_multiplier / idealMulti : 0;
            efficiency += module_efficiency;
            weighted_efficiency += module_efficiency;
            // Set module irradiance color and material
            module.irrColor = module_efficiency === 0 ? GetShadeCol(irrPro) : GetShadeCol(module_efficiency * 100);
            module.oriMat = module.material;

            UpdateModuleLabel(module);

            if (useViewerStore.getState().moduleSolarAccess) {
                SetModuleAsColored(module, module.irrColor);
            } else {
                SetModuleAsTextured(module);
            }
        });

        efficiency = module_count ? efficiency / module_count : 0;
        solar_access = module_count ? Math.round(solar_access / module_count) : 0;
        const prodInfo = multipliers.find(item => item.access === Math.floor(solar_access));
        const roof_multiplier = prodInfo?.multiplier ?? 0;
        total_multiplier += roof_multiplier * module_count;
        const actual_production = module_count * modulePower * roof_multiplier;
        const utility_production = Math.round(array_size * default_multiplier);

        treeImpactRoofArr.forEach(tree => {
            const treeSolarAvg = module_count ? Math.round(tree.solar_access / module_count) : 0;
            const treeProdInfo = multipliers.find(item => item.access === treeSolarAvg);
            const treeGhostRoofMulti = treeProdInfo?.multiplier ?? 0;
            const roofTotalMulti = treeGhostRoofMulti * module_count;

            const idx = treeGhostMultArr.findIndex(data => tree.treeId === data.treeId);
            if (idx === -1) {
                treeGhostMultArr.push({treeId: tree.treeId, total_multiplier: roofTotalMulti});
            } else {
                treeGhostMultArr[idx].total_multiplier += roofTotalMulti;
            }

        });

        totalSystemSize += (array_size / 1000);
        actualWattage += actual_production;
        utilityWattage += utility_production;

        const monthlyProdArr = computeMonthlyProduction(actual_production, monthlyWeightArr);
        monthNames.forEach((month, idx) => {
            monthlyProductionEstimateData[month].actual_production = monthlyProdArr[idx];
        })

        roofInfoArr.push({
            buildingName: roofMesh.userData.buildingName,
            roofFaceId,
            roofNum: roofMesh.userData.roof_tag_id,
            arraySize: (array_size / 1000).toFixed(3),
            azimuth: azimuth,
            pitch: tilt,
            ballastPitch: roofMesh.userData.ballastedPitch,
            ballastAzimuth: roofMesh.userData.ballastedAzimuth,
            solarAccess: solar_access,
            monthlyProductionEstimateData: monthlyProductionEstimateData,
            efficiency: Math.round(efficiency * 100),
            modules: moduleMeshArr.length.toString(),
            roofMultiplier: roof_multiplier,
            utility_production: utility_production.toFixed(1),
            actual_production: actual_production.toFixed(1),
            pitchOverride: roofMesh.userData.pitchOverride,
            nameOverride: roofMesh.userData.nameOverride,
        });
        const roofMs = perfNow() - roofT0;
        if (isModulePerfLogEnabled() && roofMs >= 120) {
            perfLog(
                'SetModuleGraph roof',
                roofMs,
                `${roofFaceId} modules=${moduleMeshArr.length}`
            );
        }
    });
    perfLog('SetModuleGraph roofMeshArr loop', perfNow() - tRoofLoop, `roofs=${roofMeshArr.length}`);

    const weightedSolarAccess = total_module_count === 0 ? 0
        : Math.round(totalWeightedSolarAccess / total_module_count);

    SetModuleCount(roofMeshArr);

    weighted_efficiency = total_module_count > 0
        ? Math.round((weighted_efficiency * 100) / total_module_count)
        : 0;

    const moduleMultiplierAverage = total_module_count === 0 ? 0 : moduleMultiplierTotal / total_module_count;
    // const multiplier = GetRoundNum(totalMultiplier / totalArea, 2);
    const multiplier = total_module_count === 0 ? 0 : GetRoundNum(total_multiplier / total_module_count, 3);

    treeGhostMultArr.forEach(tree => {
        const treeGhostMultiplier = total_module_count === 0 ? 0 : GetRoundNum(tree.total_multiplier / total_module_count, 3);
        tree.ghostedMulti = treeGhostMultiplier;
        tree.treeImpactMulti = treeGhostMultiplier - multiplier;
    });

    // Set tree impact mutipliers to each tree objects
    self.modelGroup.traverse(child => {
        if (child.meshType !== 'tree' || child.name !== 'wrapper') return;
        const {userData} = child.parent;
        const treeGhostMulti = treeGhostMultArr.find(e => e.treeId === userData.treeId);
        child.parent.treeImpact = treeGhostMulti?.treeImpactMulti ?? 0;
    });

    useViewerStore.setState({
        modeCount: total_module_count,
        roofAreaSize,
        roofInfoArr,
        multiplier,
        actualWattage: GetRoundNum(actualWattage, 4),
        totalSystemSize,
        utilityWattage,
        efficiency: weighted_efficiency,
        weightedSolarAverage: weightedSolarAccess,
        savedAnnualIdealMultiplier: multiplier,
        moduleMultiplierAverage: moduleMultiplierAverage,
    });

    syncActiveAiDesignProductionFromCard();

    const tLabels = perfNow();
    roofMeshArr.map(r => getRoofModuleMeshes(r).map(module => {
        UpdateModuleLabel(module);
    }));
    if (isGhostOnlyGreenImpactTreesEnabled() && !isAiRunMode()) {
        refreshGhostedTreesActiveTreeImpact(self);
        enforceGreenImpactTreeRules(self);
        endManualGhostToggleIfIdle();
    }
    UpdateTreeMuliplier(self.modelGroup);
    if (isGhostOnlyGreenImpactTreesEnabled() && !isAiRunMode()) {
        flushPendingUnghostAlert(self);
    }
    reapplyAllTreeVisualStates(self);
    perfLog('SetModuleGraph labels + trees', perfNow() - tLabels);
    perfLog('SetModuleGraph total', perfNow() - _perfT0);
}

function UpdateModuleLabel(module) {
    const moduleLabel = module.children.find(c => c.meshType === 'moduleLabel');
    if (moduleLabel === undefined) {
        return;
    }

    const multiplierAverage = useViewerStore.getState().moduleMultiplierAverage;
    // const multiplierAverage = useViewerStore.getState().multiplier;

    const content = `${getMultiplierLabelPrefix(module.multiplier > multiplierAverage, true, 9)}${GetRoundNum(module.multiplier, 4)}`;
    moduleLabel.element.innerHTML = content;
    moduleLabel.element.style.color = '#fff';
    moduleLabel.element.style.fontSize = "11px";
}

function CheckInShade(shade, moduleArr) {
    const {x, y} = shade.position;
    var flagIn = false;
    moduleArr.forEach(module => {
        if (flagIn) return;
        const {x0, x1, z0, z1} = module.verInfo;
        if (x > x0 && x < x1 && y > z0 && y < z1) {
            flagIn = true;
        }
    });
    return flagIn;
}

function GetIrrPro(verInfo, shadeArr, treeArr) {
    var totalVal = 0, count = 0;
    const {x0, x1, z0, z1} = verInfo;
    const treeImpactModuleArr = treeArr.map(treeId => ({ treeId, totalVal: 0 }));
    shadeArr.forEach(shade => {
        const { x, y } = shade.position;
        if (x > x0 && x < x1 && y > z0 && y < z1) {
            treeArr.forEach(treeId => {
                const ptHashGhost = shade.treeArr?.find(t => t.treeId === treeId)?.ptHashGhost ?? 0;
                const treeImpact = treeImpactModuleArr.find(t => t.treeId === treeId);
                treeImpact.totalVal += ptHashGhost;
            });
            totalVal += shade.ptHashVal ?? 0;
            count++;
        }
    });
    if (count === 0) {
        logger.error('Error shadePoint');
    }
    treeImpactModuleArr.forEach(t => {
        t.irrPro = t.totalVal / count;
    });
    const irrPro = count ? totalVal / count : 0;
    return {irrPro, treeImpactModuleArr};
}

function getIrrProForGivenMonthIdx(verInfo, shadeArr,monthIdx) {
    var totalVal = 0, count = 0;
    const {x0, x1, z0, z1} = verInfo;
    shadeArr.forEach(shade => {
        const {x, y} = shade.position;
        if (x > x0 && x < x1 && y > z0 && y < z1) {
            totalVal += (shade.monthArr?.[monthIdx] || 0) * 100;
            count++;
        }
    });
    if (count === 0) {
        logger.error('Error shadePoint');
    }

    return count ? totalVal / count : 0;
}

export function CalculateIrradiancePerRoof(self) {
    const { roofs, roofMeshArr } = self;

    const roofIrradianceData = [];

    roofMeshArr.forEach((selRoofMesh) => {
        if (selRoofMesh.disableModule) return;

        let relatedRoof = roofs.find(roof =>
            roof.faces.some(face => face.roofFaceId === selRoofMesh.roofFaceId)
        );

        if (!relatedRoof) return;

        const roofIrradianceMonthTotal = new Array(12).fill(0);
        let totalRoofArea = 0;

        relatedRoof.faces.forEach((face) => {
            if (face.roofFaceId !== selRoofMesh.roofFaceId) return;

            const { size, ptHashMonth } = face;
            totalRoofArea += size;

            if (!ptHashMonth) return;

            ptHashMonth.forEach((monthVal, monthIdx) => {
                roofIrradianceMonthTotal[monthIdx] += monthVal * size;
            });
        });

        const avrIrradiance = totalRoofArea > 0 ? roofIrradianceMonthTotal.map(monthVal =>
            Math.round((monthVal / totalRoofArea) * 100)
        ) : new Array(12).fill(0);

        roofIrradianceData[selRoofMesh.roofFaceId] = avrIrradiance;
    });

    return roofIrradianceData;
}

export async function GetFaceProdAPI(location, angArr, roofMeshArr, num, onFail) {
    const opportunityId = sessionStorage.getItem('SalesForceId');
    const lossesVendor = useViewerStore.getState().selectedLossesVendor;
    const accessoryBuildingList = useViewerStore.getState().accessoryBuildingList;

    const payload = {
        opportunityId: opportunityId,
        latitude: location.latitude,
        longitude: location.longitude,
        vendor: lossesVendor,
        roofProperties: angArr
            .map(ang => {
                return {
                    ang: ang,
                    roof: roofMeshArr.find(r =>
                        r.oriAng.azimuth === ang.azimuth &&
                        r.oriAng.tilt === ang.tilt &&
                        r.userData.roof_tag_id &&
                        r.userData.roof_size_type !== 'small_face' &&
                        !accessoryBuildingList.includes(r.userData.buildingName))
                };
            })
            .filter(item => item.roof)
            .map(item => {
                return {
                    roofTagId: item.roof.userData.roof_tag_id,
                    azimuth: GetRoundNum(item.ang.azimuth, 4),
                    tilt: GetRoundNum(item.ang.tilt, 2),
                };
            }),
    };

    if (payload.roofProperties.length === 0) {
        return;
    }

    let multiplierResponse = await tryWaitAccessMultipliers(
        payload,
        () => onFail("This site has not yet processed. Load times may be longer than usual.", false));

    if (multiplierResponse.status !== 200) {
        onFail("We couldn't get multipliers, contact support team.", true);
        return;
    }

    const multiplierResults = multiplierResponse.data.data;
    if (multiplierResults.length === 0) {
        return;
    }

    const newAngArr = cloneDeep(angArr);
    for (let i = 0; i < angArr.length; i++) {
        const {azimuth, tilt} = angArr[i];
        const multiplierResultsFiltererd = multiplierResults.find(md => md.azimuth === GetRoundNum(azimuth, 4) && md.tilt === GetRoundNum(tilt, 2));
        if (multiplierResultsFiltererd) {
            newAngArr[i].multipliers = multiplierResultsFiltererd.accessMultipliers;
        }
    }


    useViewerStore.setState({savedAccessMultipliersForSolarRoofs: newAngArr});
}

function tryWaitAccessMultipliers(payload, firstRetryCallback) {
    const operation = retry.operation({
        retries: 20,
        factor: 2,
        minTimeout: 30 * 1000,
        maxTimeout: 60 * 1000,
        randomize: true,
    });

    return new Promise((resolve, reject) => {
        operation.attempt(async attempt => {
            try {
                const response = await getAccessMultipliers(payload);
                if (response.status === 202) {
                    if (attempt === 1) {
                        firstRetryCallback();
                    }
                    if (operation.retry(response.status)) {
                        return;
                    }
                }

                resolve(response);
            } catch (e) {
                reject(e);
            }
        });
    });
}

export async function GetFaceIdealAPI(location, angArr) {
    const opportunityId = sessionStorage.getItem('SalesForceId');
    const localAngIdeal = useViewerStore.getState().savedRoofWiseIdealMultiplier;

    for (const ang of angArr) {
        const {latitude, longitude} = location;
        if (!ang) continue;
        const {tilt, ideal} = ang;
        if (ideal) continue;

        let idealAngData = localAngIdeal.find(itm => (itm.tilt === tilt));

        if (idealAngData && idealAngData.ideal && idealAngData.monthlyMultipliers) {
            ang.ideal = idealAngData.ideal;
            ang.idealMonthly = idealAngData.monthlyMultipliers;
        } else {
            const idealMult = await getIdealMultiplier(opportunityId, latitude, longitude, tilt);
            ang.ideal = idealMult.multiplier;
            ang.idealMonthly = idealMult.monthlyMultipliers;
        }
    }

    useViewerStore.setState({savedRoofWiseIdealMultiplier: angArr});
}

export function CheckSingleModuleRoofs(roofMeshArr) {
    return roofMeshArr.filter(r => getRoofModuleCount(r) === 1)
        .map(r => r.userData.nameOverride ?? r.userData.roof_tag_id);
}

export function SetModuleCount(roofMeshArr) {
    var verCount = 0, horCount = 0;
    roofMeshArr.forEach(roofMesh => {
        const moduleArr = getRoofModuleMeshes(roofMesh);
        const verC = moduleArr.filter(item => {
            return item.dir === 'port'
        }).length, horC = moduleArr.length - verC;
        verCount += verC;
        horCount += horC;
    });
    const edgeCount = useViewerStore.getState().savedRoofEdgeTypeCount;

    sessionStorage.setItem("setbackInfo", JSON.stringify(edgeCount));

    useViewerStore.setState({setbackInfoObject: edgeCount});
}

export function SaveEdgeCount(edgeArr) {
    const edgeCount = {ridge: 0, valley: 0, eave: 0, rake: 0, other: 0};
    edgeArr.forEach(edge => {
        if (edge.type === 'ridge') edgeCount.ridge++;
        else if (edge.type === 'valley') edgeCount.valley++;
        else if (edge.type === 'eave') edgeCount.eave++;
        else if (edge.type === 'rake') edgeCount.rake++;
        else edgeCount.other++;
    });
    useViewerStore.setState({savedRoofEdgeTypeCount: edgeCount});
}

export function SetClearFaceModule(self, faceMesh) {
    const selModules = faceMesh.children.filter(item => item.module);
    DeleteModule(selModules, faceMesh, self.moduleMeshArr, self);
}


export function DeleteSingleModuleOnRoof(self) {
    const {roofMeshArr} = self;
    const singleModuleRoofs = CheckSingleModuleRoofs(roofMeshArr);
    if (singleModuleRoofs.length === 0) return;
    singleModuleRoofs.forEach((roofID) => {
        const selRoof = roofMeshArr.find(r => Number(r.userData.roof_tag_id) === Number(roofID));
        if (!selRoof) return;
        const selModules = getRoofModuleMeshes(selRoof);
        DeleteModule(selModules, selRoof, self.moduleMeshArr, self);
    })


}

export function FlipDir(dir) {
    return dir === 'land' ? 'port' : 'land';
}

function cloneSavedRoofModulesLayout() {
    return JSON.parse(JSON.stringify(useViewerStore.getState().savedRoofModulesLayout || []));
}
function syncBallastedModulesToLayout(self, layout) {
    const moduleMap = new Map();
    self.moduleMeshArr.forEach((module) => {
        moduleMap.set(module.moduleId, module);
    });
    layout.forEach((roofInfo) => {
        roofInfo.moduleArr?.forEach((moduleInfo) => {
            const moduleMesh = moduleMap.get(moduleInfo.moduleId);
            if (!moduleMesh) return;
            moduleInfo.isBallastedRackType = !!moduleMesh.userData?.isBallastedRackType;
            if (!moduleInfo.isBallastedRackType) return;
            moduleInfo.ballastRackDirection = moduleMesh.userData.ballastRackDirection;
            moduleInfo.ballastAngle = moduleMesh.userData.ballastAngle;
            moduleInfo.isBallastFlipped = !!moduleMesh.userData.isBallastFlipped;
            moduleInfo.ballastedAzimuth = moduleMesh.userData.ballastedAzimuth;
            moduleInfo.ballastedPitch = moduleMesh.userData.ballastedPitch;
            if (moduleMesh.userData.ballastRackDirection === 'custom') {
                moduleInfo.azimuth = moduleMesh.userData.ballastedAzimuth ?? moduleMesh.userData.azimuth;
            } else {
                delete moduleInfo.azimuth;
            }
        });
    });
    return layout;
}
function addModuleLayoutStep(self, oldLayout, newLayout) {
    const oldLayoutStr = JSON.stringify(oldLayout);
    const newLayoutStr = JSON.stringify(newLayout);
    if (oldLayoutStr === newLayoutStr) return;
    useViewerStore.setState({ savedRoofModulesLayout: newLayout });
    self.addStep(EditAction.ModuleLayout, 1, oldLayoutStr, newLayoutStr);
}

export const addBallastedRackedModules = (ballastedAngle, ballastedAzimuth)=>{
    const sceneViewer = useViewerStore.getState().sceneViewer;

    const isBallastedRackTypeModuleActive = useViewerStore.getState().isBallastedRackTypeModuleActive;
    const financePartnerName = useViewerStore.getState().financePartnerDetails?.name?.toLowerCase().trim() || '';
    const selectedRoofs = useViewerStore.getState().selRoofFaceArr || [];

    if(!sceneViewer) return;

    document.body.style.cursor = "default";

    useViewerStore.setState({isMoveModuleActive: false, isAddModuleActive: false});

    if (!isBallastedRackTypeModuleActive) {
        const selectedRoofList = selectedRoofs.length
            ? selectedRoofs
            : sceneViewer.roofMeshArr.filter(roof => roof.selectRoofFace !== false);
        const hasLowPitchSelectedRoof = selectedRoofList.some(roof => Number(roof?.userData?.pitchOverride ?? roof?.oriAng?.tilt ?? 0) <= 5);
        if (isLightReachFinancePartner(financePartnerName) && hasLowPitchSelectedRoof) {
            syncLightReachSitePitchWarning(sceneViewer);
        }

        sceneViewer.selectType = 'module';
        sceneViewer.setMoveMode(false);
        sceneViewer.ballastAzimuth = ballastedAzimuth;
        sceneViewer.ballastAngle = ballastedAngle;
        // const activeBallastRackDirection = useViewerStore.getState().activeBallastRackDirection;
        // sceneViewer.moduleDir =  'land'; //activeBallastRackDirection === 'vertical' ? 'port' :  "land";
        sceneViewer.setPushMode(true);
        useViewerStore.setState({isBallastedRackTypeModuleActive: true})
    } else {
        sceneViewer.setPushMode(false);
        useViewerStore.setState({isBallastedRackTypeModuleActive: false})
    }
}

export const flipBallastTypeModule =(self,flipGivenModule=null)=>{
    const shouldTrackStep = flipGivenModule === null;
    const oldLayout = shouldTrackStep ? cloneSavedRoofModulesLayout() : null;

    const ballastedRackModules = self.moduleMeshArr.filter(item => item.userData.isBallastedRackType)

    const selModules = GetSelModules(self.moduleMeshArr);

    if(selModules.length === 0) {

        //FLIP ALL BALLAST MODULES

        ballastedRackModules.forEach(module => {

            const associatedRoof = self.roofMeshArr.find(r => r.roofFaceId === module.roofId);

            const associatedRoofPitch = Number(associatedRoof?.oriAng?.tilt || 0)
            const currentBallastAngle = module.userData.ballastAngle
            const normalizedCurrentBallastAngle = currentBallastAngle - associatedRoofPitch;

            const ballastDirection = module.userData.ballastRackDirection
            if (ballastDirection === 'horizontal') {

                if(!module.userData.isBallastFlipped){
                    module.rotation.x = (normalizedCurrentBallastAngle * Math.PI / 180);
                }
                else{
                    module.rotation.x = (-normalizedCurrentBallastAngle * Math.PI / 180);
                }
                module.userData.isBallastFlipped = !module.userData.isBallastFlipped;
            } else if (ballastDirection === 'vertical') {
                if(!module.userData.isBallastFlipped){
                    module.rotation.y = (normalizedCurrentBallastAngle * Math.PI / 180);
                }
                else{
                    module.rotation.y = (-normalizedCurrentBallastAngle * Math.PI / 180);
                }

                module.userData.isBallastFlipped = !module.userData.isBallastFlipped;
            }
        })
    }
    else {
        //FLIP SELECTED BALLAST MODULES


        selModules.forEach(module => {

            const associatedRoof = self.roofMeshArr.find(r => r.roofFaceId === module.roofId);

            const associatedRoofPitch = Number(associatedRoof?.oriAng?.tilt || 0)

            const currentBallastAngle = module.userData.ballastAngle
            const normalizedCurrentBallastAngle = currentBallastAngle - associatedRoofPitch;

            if (module.userData.isBallastedRackType) {
                const ballastDirection = module.userData.ballastRackDirection
                if (ballastDirection === 'horizontal') {
                    if(!module.userData.isBallastFlipped){
                        module.rotation.x = (normalizedCurrentBallastAngle * Math.PI / 180);
                    }
                    else{
                        module.rotation.x = (-normalizedCurrentBallastAngle * Math.PI / 180);
                    }
                    module.userData.isBallastFlipped = !module.userData.isBallastFlipped;
                } else if (ballastDirection === 'vertical') {
                    if(!module.userData.isBallastFlipped){
                        module.rotation.y = (normalizedCurrentBallastAngle * Math.PI / 180);
                    }
                    else{
                        module.rotation.y = (-normalizedCurrentBallastAngle * Math.PI / 180);
                    }
                    module.userData.isBallastFlipped = !module.userData.isBallastFlipped;
                }
            }
        })

    }

    self.viewerAlert({
        show: true,
        title: "Ballast Modules Flipped",
        message: "Selected ballasted racking modules have been successfully flipped.",
        messageType: "info",
        isModal: false,
        isCloseAfterDisplay : true
    })

    if(flipGivenModule!==null){

        ballastedRackModules.forEach(module => {

            const ballastDirection = module.userData.ballastRackDirection
            if (ballastDirection === 'horizontal') {
                if(!module.userData.isBallastFlipped){
                    module.rotation.x = (module.userData.ballastAngle * Math.PI / 180);
                }
                else{
                    module.rotation.x = (-module.userData.ballastAngle * Math.PI / 180);
                }
                module.userData.isBallastFlipped = !module.userData.isBallastFlipped;
            } else if (ballastDirection === 'vertical') {
                if(!module.userData.isBallastFlipped){
                    module.rotation.y = (module.userData.ballastAngle * Math.PI / 180);
                }
                else{
                    module.rotation.y = (-module.userData.ballastAngle * Math.PI / 180);
                }
                module.userData.isBallastFlipped = !module.userData.isBallastFlipped;
            }
        })
    }
    if (shouldTrackStep && oldLayout) {
        const newLayout = syncBallastedModulesToLayout(self, cloneSavedRoofModulesLayout());
        addModuleLayoutStep(self, oldLayout, newLayout);
    }
}

export function updateBallastedRackAngleForSelectedRoofs(self, roofMesh, newBallastAngle) {
    if (!Number.isFinite(newBallastAngle) || newBallastAngle < MIN_BALLAST_RACKING_ANGLE) return;
    self.ballastAngle = newBallastAngle;
    roofMesh.userData.ballastedPitch = newBallastAngle;
    const oldLayout = cloneSavedRoofModulesLayout();
    const allBallastedModules = self.moduleMeshArr.filter((module) => module.userData?.isBallastedRackType && module.roofId === roofMesh.roofFaceId);
    allBallastedModules.forEach((module) => {
        // const shapeMesh = module.parent;
        updateBallastedRackAngle(module, roofMesh, newBallastAngle);
    });
    // useViewerStore.setState({ ballastedRackModuleAngle: newBallastAngle });
    const newLayout = syncBallastedModulesToLayout(self, useViewerStore.getState().savedRoofModulesLayout);
    addModuleLayoutStep(self, oldLayout, newLayout);
}

export const check25PercentRuleForBallastedRacking =(moduleMeshArr)=>{

    const ballastedRackModules = moduleMeshArr.filter(item => item.userData.isBallastedRackType)
    const normalModules = moduleMeshArr.filter(item => !item.userData.isBallastedRackType)

    const sceneViewer = useViewerStore.getState().sceneViewer;

    //if ballasted modules is not 25% of system size of normal modules, then we need to show a warning
    if(ballastedRackModules.length > 0 && normalModules.length === 0 || ballastedRackModules.length === 0 && normalModules.length === 0){
        //if normalModules.length === 0) then ballastedRackModules.length has to be minimum 5
        if(normalModules.length === 0 && ballastedRackModules.length < 5){
            useViewerStore.setState({isBallastedRackingMeeting25percentRule: false});
            sceneViewer.viewerAlert({
                title: '25% Rule Not Met for Ballasted Racking',
                message: 'The ballasted modules are less than 5. Please add more ballasted modules to the system.',
                messageType: 'error',
                isCloseAfterDisplay: true,
                autoCloseSeconds: 1000,

            })
        }

        if(normalModules.length === 0 && ballastedRackModules.length >= 5){
            useViewerStore.setState({isBallastedRackingMeeting25percentRule: true});
            sceneViewer.viewerAlert({
                title: '25% Rule Met for Ballasted Racking',
                message: 'The ballasted modules are greater than 5. The 25% rule is met.',
                messageType: 'success',
                isCloseAfterDisplay: true,
                autoCloseSeconds: 2000
            })
        }

    }
    else if(ballastedRackModules.length > 0 && normalModules.length > 0){



        const normalModuleCount = normalModules.length;
        const ballastedRackModuleCount = ballastedRackModules.length;
        const ballastedRackModulePercentage = (ballastedRackModuleCount / normalModuleCount) * 100;
        if(ballastedRackModulePercentage < 25){
            useViewerStore.setState({isBallastedRackingMeeting25percentRule: false});
            sceneViewer.viewerAlert({
                title: '25% Rule Not Met for Ballasted Racking',
                message: 'The percentage of ballasted modules is less than 25% of the system size. Please add more ballasted modules to the system.',
                messageType: 'error',
                isCloseAfterDisplay: true,
                autoCloseSeconds: 1000

            })
        }
        else {
            sceneViewer.viewerAlert({
                title: '25% Rule Met for Ballasted Racking',
                message: 'The percentage of ballasted modules is greater than 25% of the system size.',
                messageType: 'success',
                isCloseAfterDisplay: true,
                autoCloseSeconds: 2000

            })

            useViewerStore.setState({isBallastedRackingMeeting25percentRule: true});
        }
    }

    useViewerStore.setState({isBallastedRackUpdated: !useViewerStore.getState().isBallastedRackUpdated});

}

export const resetModuleInstances = (self) => {
    self.moduleMeshArr.forEach(module => {
        module.reset()
    })
}

export const setBuildingAsDetachedStructure =(flagValue, selectedRoofMesh=null)=>{
    const sceneViewer = useViewerStore.getState().sceneViewer;

    let {preSelRoofId, roofMeshArr} = sceneViewer;

    if(selectedRoofMesh!==null){
        preSelRoofId = selectedRoofMesh.roofFaceId;
    }
    const selRoofMesh = roofMeshArr.find(item => item.roofFaceId === preSelRoofId);

    const buildingName = selRoofMesh.userData.buildingName;

    if(flagValue){
        if(!useViewerStore.getState().detachedStructureBuildingIds.includes(Number(buildingName))){
            useViewerStore.setState({detachedStructureBuildingIds : [...useViewerStore.getState().detachedStructureBuildingIds, Number(buildingName)]})
        }
    }
    else{
        useViewerStore.setState({detachedStructureBuildingIds : useViewerStore.getState().detachedStructureBuildingIds.filter(id => id !== Number(buildingName))})

    }

    toggleSetbackIfMarkedDetachedForGivenBuilding(buildingName , flagValue);

    sceneViewer.viewerAlert({
        show: true,
        title: "Detached Structure"+ (flagValue ? " Set" : " Unset"),
        message: `Selected Building ID : ${buildingName} is ${flagValue ? "" : "no longer"} set as Detached Structure.`,
        messageType: AlertType.info,
        isModal: false,
        isCloseAfterDisplay: true,
    })
}


export const isGivenRoofPartOfDetachedStructure = (roofMesh)=>{
    const buildingName = roofMesh.userData.buildingName;
    return useViewerStore.getState().detachedStructureBuildingIds.includes(Number(buildingName));
}